import { MarketScanCompetitor, MarketScanDetailsScreenMatrixModel, MarketScanMatrixModel, MeasureCaptureFor } from './../../classes/market-scan/market-scan.class';
import { Injectable } from "@angular/core";
import { cloneDeep, difference, differenceWith, every, isEmpty, isEqual, isInteger, map, remove, unionBy, uniqWith } from 'lodash-es';
import { _ } from '@omni/utility/lodash-chain';
import { BehaviorSubject } from "rxjs";
import { FeatureActionsMap } from "../../classes/authentication/user.class";
import { Brand } from "../../classes/brand/brand.class";
import {
  Action,
  CustomerSegmentation,
  FilterType,
  MarketScan,
  MarketScanContact,
  MarketScanCustomerSegment,
  MarketScanProduct,
  MarketScanStateCode,
  MarketScanStatusCode,
  MarketScanTA,
  MarketScanCustomerData,
  Category,
  Measure,
  CreateFrequency,
  SubRowData,
  MeasureType,
  CaptureData,
  CustomerJourney,
  Event,
  AffiliatedAccount, CaptureDataFor, MarketScanAccount
} from "../../classes/market-scan/market-scan.class";
import { Endpoints } from "../../../config/endpoints.config";
import {
    DB_ALLDOCS_QUERY_OPTIONS,
    DB_KEY_PREFIXES,
    DB_SYNC_STATE_KEYS,
    PREFIX_SEARCH_ENDKEY_UNICODE
} from "../../config/pouch-db.config";
import { DeltaService, EntityNames, EntitySyncInfo } from "../../data-services/delta/delta.service";
import { MarketScanDataService } from "../../data-services/market-scan/market-scan.data.service";
import { AuthenticationService } from "../authentication.service";
import { ContactOfflineService } from "../contact/contact.service";
import { DeviceService } from "../device/device.service";
import { DiskService, OFFLINE_DATA_COUNT_ENTITY_NAME, OFFLINE_DB_LINKED_ENTITY_NAME } from "../disk/disk.service";
import { TranslateService } from "@ngx-translate/core";
import { AlertService } from '../../services/alert/alert.service';
import { NotificationService, ToastStyle } from "../notification/notification.service";
import { DateTimeFormatsService } from '../date-time-formats/date-time-formats.service';
import { SyncFeatureCategory } from '../../enums/delta-service/delta-service.enum';
import { EventsService } from "../events/events.service";
import { AccountOfflineService } from "../account/account.offline.service";
import { SurgeryOrderActivityDataService } from '@omni/data-services/surgery-order-activity/surgery-order-activity.data.service';
import { OpportunityManagementService } from '../opportunity-management/opportunity-management.service';
import { OpportunityManagementDataService } from '@omni/data-services/opportunity-management/opportunity-management.data.service';
import { SyncProfilerService } from '../logging/sync-profiler.service';
import { ProductRatingOfflineService } from '../product-rating/product-rating-offline.service';
import { CustomerSegmentationOfflineService } from '../customer-segmentation/customer-segmentation-offline.service';

@Injectable({
    providedIn: 'root'
})
export class MarketScanService {
    private marketScans = new BehaviorSubject<MarketScan[]>([]);
    public marketScansObs$ = this.marketScans.asObservable();
    public customerSegmentations: CustomerSegmentation[] = [];
    // Customer segmentation is now awaited as part of core sync (MasterDataService's contacts-
    // adjacent stage — see MarketScanService.syncCustomerSegmentationsOnly), so this reaches
    // 'ready' quickly. Kept for consumers that render before that stage resolves.
    public readonly customerSegmentationsSyncState$ = new BehaviorSubject<'idle' | 'syncing' | 'ready' | 'error'>('idle');
    // Adoption ladder stays fire-and-forget, kicked off after the whole sync sequence finishes
    // (see MasterDataService's post-promisePool call to startLadderBackgroundSync) since it alone
    // can take 2+ minutes. Consumers that read adoptionLadderRatings before this flips to 'ready'
    // may be looking at stale/prior-sync (or no) data.
    public readonly ladderSyncState$ = new BehaviorSubject<'idle' | 'syncing' | 'ready' | 'error'>('idle');
    public adoptionLadderRatings: any[] = [];
    private ladderSyncInProgress: Promise<void> | null = null;
    public productToEventsMap = {};
    public productToCampaignsMap = {};
    public contactToAccounts: any[] = [];
    public categories: Category[] = [];
    public brands: Brand[] = [];
    public therapeuticAreasMappedData = [];
    private currentMarketScanSource = new BehaviorSubject<MarketScan>(undefined);
    currentMarketScanObserver = this.currentMarketScanSource.asObservable();
    marketScanCopy: MarketScan;
    private rowData: any[] = [];
    private hasEvents: boolean = false;
    private hasCampaigns: boolean = false;
    private hasAccounts: boolean = false;
    private fetchEventsAttribute: boolean = false;
    private fetchCampaignsAttribute: boolean = false;
    public currentMarketScanMatrixData:Array<MarketScanMatrixModel> = [];
    public currentMarketScanDetailsViewMatrix:MarketScanDetailsScreenMatrixModel;

    constructor(
      private authService: AuthenticationService,
        private diskService: DiskService,
        private marketScanDataService: MarketScanDataService,
        private deltaService: DeltaService,
        private deviceService: DeviceService,
        public translate: TranslateService,
        private contactService: ContactOfflineService,
        private alertService: AlertService,
        private events: EventsService,
        private notificationService: NotificationService,
        private surgeryOrderDataService: SurgeryOrderActivityDataService,
        private readonly opportunityService: OpportunityManagementService,
        private readonly opportunityDataService: OpportunityManagementDataService,
        private accountService: AccountOfflineService,
        private dateTimeFormatsService: DateTimeFormatsService,
        private syncProfilerService: SyncProfilerService,
        private readonly productRatingService: ProductRatingOfflineService,
        private readonly customerSegmentationService: CustomerSegmentationOfflineService) {
        this.events.subscribe("marketScan:newAccountContactAff", data => {
          if (this.authService.hasFeatureAction(FeatureActionsMap.CUSTOMER_SCAN_TOOL) && this.hasAccounts && !isEmpty(data)) {
            data.forEach(d => {
              if (!d.indskr_accounttype) {
                const account = this.accountService.getAccountById(d.accountId);
                if (account && (account.raw?.indskr_accounttype == '100000001' || account.raw?.indskr_accounttype == '100000004')) {
                  d.indskr_accounttype = account.raw.indskr_accounttype;
                  d.accountName = account.accountName;
                }
              }
              //Add only if account type=Pharmacy/Other
              if (d.indskr_accounttype == '100000001' || d.indskr_accounttype == '100000004')
                this.contactToAccounts.push(d);
            })
            this.diskService.updateOrInsert(DB_KEY_PREFIXES.MARKET_SCAN_ACCOUNTS, doc => ({
              raw: this.contactToAccounts
            })).catch((err) =>
              console.error("marketScan:newAccountContactAff: Error saving accounts data to offline db!", err)
            );
          }
        })
    }

    public setCurrentMarketScan(input) {
        this.currentMarketScanSource.next(input);
    }

    public getAllMarketScans(): MarketScan[] {
        return this.marketScans.getValue();
    }

    public getSelectedMarketScan(): MarketScan {
        return this.currentMarketScanSource.getValue();
    }

    public async initNewMarketScan() {
        this.revertStatus();
        const payload = {
          indskr_externalid: "offline_marketScan_" + new Date().getTime(),
          indskr_name: this.translate.instant('CUSTOMER_SCAN'),
          indskr_date: new Date().getTime().toString(),
          indskr_enddate: new Date().getTime().toString(),
          ownerId: this.authService.user.systemUserID,
          statecode: MarketScanStateCode.Active,
          statuscode: MarketScanStatusCode.Active,
          indskr_positionid: this.authService.user.xPositionID,
        };
        const marketScan = new MarketScan(payload);
        this.setCurrentMarketScan(marketScan);
        await this.updateMarketScanInPouchDB(marketScan);
        this.upsertMarketScans(marketScan);
        return marketScan;
    }

    public async updateMarketScanInPouchDB(marketScan: MarketScan) {
        if (marketScan.isModified) {
            marketScan.modifiedon = new Date().getTime().toString();
        }
        await this.diskService.upsertMarketScan(marketScan);
    }

    upsertMarketScans(data: MarketScan) {
        //Keep only meta data in list
        const marketScan: MarketScan = this.getOnlyMetaData(data);
        let marketScans: MarketScan[] = this.marketScans.getValue();
        let offIdx: number = marketScans.findIndex((e: MarketScan) => {
            if (e.indskr_externalid) {
                return data.indskr_externalid === e.indskr_externalid
            }
            else
                return false;
        });
        if (offIdx > -1) {
            marketScans[offIdx] = marketScan;
        }
        else {
            marketScans.push(marketScan);
        }
        this.marketScans.next(marketScans);
    }

    private getOnlyMetaData(data: MarketScan) {
        const marketScan: MarketScan = new MarketScan(data);
        marketScan._id = data._id;
        marketScan.lastUpdatedTime = data.lastUpdatedTime;
        marketScan.pendingPushToDynamics = data.pendingPushToDynamics;
        marketScan.isModified = data.isModified;
        marketScan.retainPage = data.retainPage;
        marketScan.category = data.category;
        return marketScan;
    }

    private async loadAndMapMarketScanMasterDataFromDB() {
        if (this.authService.hasFeatureAction(FeatureActionsMap.CUSTOMER_SCAN_TOOL)) {
            await Promise.all([
                this.loadMarketScansFromDB(),
                this.loadProductsFromDB(),
            ]);
            this.prepareAllFilterToTherapeuticArea();
        }
    }

    private prepareAllFilterToTherapeuticArea() {
        this.therapeuticAreasMappedData = [];
        this.brands.forEach(brand => {
            // Reset (not just lazily initialize) so this is safe to call more than once per
            // sync — mapCustomerSegToProducts pushes into this array without de-duping, so
            // calling it again on a non-empty array would duplicate every contact entry.
            // Re-running is needed now that customer segmentations sync in the background and
            // this mapping has to catch up once that fetch actually completes.
            brand['customerSegmentations'] = [];
            //Map customer segmentation to brands
            this.mapCustomerSegToProducts(brand);
            if (brand.therapeuticAreas) {
                //Map brands to therapeutic areas
                this.mapBrandsToTherapeuticAreas(brand);
            }
        });

        this.therapeuticAreasMappedData = this.sortListByFielName(this.therapeuticAreasMappedData, 'therapeuticarea_name');
    }

    private mapCustomerSegToProducts(brand: Brand) {
        this.customerSegmentations.filter(seg => seg.productId === brand.ID).forEach(segm => {
            let contact = {
                contactId: segm.contactId,
                contact_fullName: this.contactService.getContactByID(segm.contactId)?.fullName,
                action: Action.PATCH
            };
            const index = brand['customerSegmentations'].findIndex(brandSegment => brandSegment.customerSegmentationId === segm.indskr_customersegmentationid);
            if (index >= 0) {
                //Customer segmentation is present, add contact
                brand['customerSegmentations'][index]['contacts'].push(contact);
            }
            else {
                //Add Customer segmentation with contact
                let segmentation = {
                    customerSegmentationId: segm.indskr_customersegmentationid,
                    segmentName: segm.indskr_name,
                    action: Action.PATCH,
                    contacts: [
                        contact
                    ]
                };
                brand['customerSegmentations'].push(segmentation);
            }
        });
    }

    private mapBrandsToTherapeuticAreas(brand: Brand) {
        brand.therapeuticAreas.forEach(ta => {
            if (!ta['brands'])
                ta['brands'] = [];
            const index = this.therapeuticAreasMappedData.findIndex(therapeuticArea => therapeuticArea.therapeuticareaid == ta.therapeuticareaid);
            let product = {
                productId: brand.ID,
                productName: brand.name,
                action: Action.PATCH,
                customerSegmentations: brand['customerSegmentations']
            };
            if (index >= 0) {
                this.therapeuticAreasMappedData[index]['brands'].push(product);

            }
            else {
                ta['brands'].push(product);
                this.therapeuticAreasMappedData.push(ta);
            }
        });
    }

    private async loadProductsFromDB() {
        await this.diskService.retrieve(DB_KEY_PREFIXES.CASE_PRODUCTS, true).then((data: any[]) => {
            if (data && data['raw']) {
                this.brands = data['raw'].map(e => new Brand(e));
                this.brands = this.sortListByFielName(this.brands, 'name');
            }
            console.log(`Brands from disk : ${this.brands ? this.brands.length : 0}`);
        });

    }

    public async syncMarketScanMasterData(dataRange: { from: string, to: string }, loadFromDbOnly = false, fullSync, parentSpanId?: string) {
        // Segmentation and adoption-ladder sync no longer happen here — segmentation is awaited
        // alongside the contacts master-data fetch (see MasterDataService, calls
        // MarketScanService.syncCustomerSegmentationsOnly) and adoption ladder is kicked off
        // fire-and-forget after the whole sync sequence finishes (MasterDataService, calls
        // startLadderBackgroundSync). See CustomerSegmentationOfflineService/
        // ProductRatingOfflineService for the split fetch paths.
        if (this.authService.hasFeatureAction(FeatureActionsMap.CUSTOMER_SCAN_TOOL)) {
            this.deltaService.pushSyncEntityName(SyncFeatureCategory.profiles);
            await Promise.all([
              this.syncProfilerService.timeAsync('market_scan_create_frequency', () => this.syncCreateFrequency(), parentSpanId),
              this.syncProfilerService.timeAsync('market_scan_competitors_data', () => this.opportunityDataService.getCompetitorsData(fullSync,loadFromDbOnly,!this.authService.hasFeatureAction(FeatureActionsMap.OPPORTUNITY_COMPETITOR_DATA)), parentSpanId),
              this.syncProfilerService.timeAsync('market_scan_customer_data_attributes_and_scans', () => this.syncCustomerDataAttributes(loadFromDbOnly).then(async () => {
                await this.syncMarketScans(dataRange, loadFromDbOnly);
              }), parentSpanId),
              this.syncProfilerService.timeAsync('market_scan_categories_and_related', () => this.syncMarketScanCategories(loadFromDbOnly).then(async () => {
                let tasks = []
                if (this.hasEvents)
                    tasks.push(this.syncEvents(loadFromDbOnly))
                if (this.hasCampaigns)
                    tasks.push(this.syncCampaigns(loadFromDbOnly))
                if (this.hasAccounts)
                    tasks.push(this.syncAccounts(loadFromDbOnly))
                await Promise.all(tasks);
              }), parentSpanId)
            ])
            this.loadAndMapMarketScanMasterDataFromDB();
        }
    }

    /**
     * Kicks off the adoption-ladder fetch in the background, without blocking the caller. Meant
     * to be called fire-and-forget after the entire sync sequence finishes (see MasterDataService,
     * right after promisePool(syncTasks) resolves) — NOT awaited by sync:completed. Guards against
     * overlapping runs (e.g. user-triggered refresh while a sync is already in flight) via
     * ladderSyncInProgress. Returns the in-flight promise so callers may still attach a .catch()
     * without awaiting it.
     */
    public startLadderBackgroundSync(loadFromDbOnly: boolean, forceFullSync: boolean, parentSpanId?: string): Promise<void> {
        if (this.ladderSyncInProgress) {
            return this.ladderSyncInProgress;
        }
        this.ladderSyncState$.next('syncing');
        // Manual, profiler-session-independent timing: by the time this resolves, the main sync's
        // SyncProfilerService session has very likely already ended (endSession() clears its spans
        // map once the rest of sync finishes), so the timeAsync span below may end up orphaned —
        // its endSpan() call would find nothing to update. This log is the reliable fallback.
        const backgroundSyncStartTime = performance.now();
        this.ladderSyncInProgress = this.syncProfilerService.timeAsync(
            'adoption_ladder_sync', () => this.syncAdoptionLadderRatings(loadFromDbOnly, forceFullSync), parentSpanId)
          .then(() => {
              console.log(`[adoption_ladder_sync] background fetch completed in ${Math.round(performance.now() - backgroundSyncStartTime)}ms`);
              this.ladderSyncState$.next('ready');
          })
          .catch((error) => {
              console.error(`[adoption_ladder_sync] background fetch failed after ${Math.round(performance.now() - backgroundSyncStartTime)}ms`, error);
              this.ladderSyncState$.next('error');
          })
          .finally(() => {
              this.ladderSyncInProgress = null;
          });
        return this.ladderSyncInProgress;
    }

    public async syncEvents(loadFromDbOnly = false) {
        let offlineFallback: boolean = this.deviceService.isOffline || loadFromDbOnly;
        if (offlineFallback) {
            await this.loadEventsFromDB();
        } else {
            const startDate: string = this.dateTimeFormatsService.getStartOfYear(new Date()).getTime().toString();
            const endDate: string = this.getEndDateForQuater();
            await this.marketScanDataService.getEvents(startDate, endDate).then(async (data) => {
                if (data) {
                    this.productToEventsMap = data;
                    await this.diskService.updateOrInsert(DB_KEY_PREFIXES.MARKET_SCAN_EVENTS, doc => ({ raw: data })).catch((err) => console.error("syncEvents: Error saving events data to offline db!", err));
                }
            }).catch((err) => {
                console.error("syncEvents: Error fetching events!", err);
            });
        }
    }

    private getEndDateForQuater(): string {
        const currentMonth: number = new Date().getMonth();
        let month = 12;
        if (currentMonth <= 1) {
            //When user in Jan/ Feb, fetch data only till March
            month = 2;
        } else if (currentMonth > 1 && currentMonth <= 4) {
            //When user in March, fetch next quater records to support offline
            month = 5;
        } else if (currentMonth > 4 && currentMonth <= 7) {
            //When user in June, fetch next quater records to support offline
            month = 8;
        } else {
            //When user in Sept, fetch next quater records to support offline
            month = 12;
        }
        return this.dateTimeFormatsService.getEndOfMonth(new Date(new Date().setMonth(month))).getTime().toString();
    }

    public async syncCampaigns(loadFromDbOnly = false) {
        let offlineFallback: boolean = this.deviceService.isOffline || loadFromDbOnly;
        if (offlineFallback) {
            await this.loadCampaignsFromDB();
        } else {
            const startDate: string = this.dateTimeFormatsService.getStartOfYear(new Date()).getTime().toString();
            const endDate: string = this.getEndDateForQuater();
            await this.marketScanDataService.getCampaigns(startDate, endDate).then(async (data) => {
                if (data) {
                    this.productToCampaignsMap = data;
                    await this.diskService.updateOrInsert(DB_KEY_PREFIXES.MARKET_SCAN_CAMPAIGNS, doc => ({ raw: data })).catch((err) => console.error("syncCampaigns: Error saving campaigns data to offline db!", err));
                }
            }).catch((err) => {
                console.error("syncCampaigns: Error fetching campaigns!", err);
            });
        }
    }

    public async syncAccounts(loadFromDbOnly = false) {
      let offlineFallback: boolean = this.deviceService.isOffline || loadFromDbOnly;
      if (offlineFallback) {
        await this.loadAccountsFromDB();
      } else {
        await this.marketScanDataService.getAffiliatedAccounts().then(async (data) => {
          if (data) {
            this.contactToAccounts = data;
            await this.diskService.updateOrInsert(DB_KEY_PREFIXES.MARKET_SCAN_ACCOUNTS, doc => ({ raw: data })).catch((err) => console.error("syncAccounts: Error saving accounts data to offline db!", err));
          }
        }).catch((err) => {
          console.error("syncAccounts: Error fetching accounts!", err);
        });
      }
    }

    private async loadEventsFromDB() {
        await this.diskService.retrieve(DB_KEY_PREFIXES.MARKET_SCAN_EVENTS).then((data) => {
            if (data && data['raw']) {
                this.productToEventsMap = data['raw'];
                console.log(`Events from disk : ${data['raw'].length}`);
            }
        }).catch((err) => console.error("loadEventsFromDB: Error saving events data to offline db!", err));
    }

    private async loadCampaignsFromDB() {
        await this.diskService.retrieve(DB_KEY_PREFIXES.MARKET_SCAN_CAMPAIGNS).then((data) => {
            if (data && data['raw']) {
                this.productToCampaignsMap = data['raw'];
                console.log(`Campaigns from disk : ${data['raw'].length}`);
            }
        }).catch((err) => console.error("loadCampaignsFromDB: Error saving campaigns data to offline db!", err));
    }

    private async loadAccountsFromDB() {
      await this.diskService.retrieve(DB_KEY_PREFIXES.MARKET_SCAN_ACCOUNTS).then((data) => {
        if (data && data['raw']) {
          this.contactToAccounts = data['raw'];
          console.log(`Accounts from disk : ${data['raw'].length}`);
        }
      }).catch((err) => console.error("loadAccountsFromDB: Error saving accounts data to offline db!", err));
    }

    private async syncCustomerDataAttributes(loadFromDbOnly = false) {
        let offlineFallback: boolean = this.deviceService.isOffline || loadFromDbOnly;
        if (!offlineFallback) {
            try {
                const attributes = await this.marketScanDataService.getCustomerDataAttributes();
                if (attributes) {
                    this.fetchEventsAttribute = attributes.hasEvents;
                    this.fetchCampaignsAttribute = attributes.hasCampaigns
                } else {
                    this.fetchEventsAttribute = false;
                    this.fetchCampaignsAttribute = false;
                }
            } catch (error) {
                console.log("Error retreiving marketScanCreateFrequency ", error);
            }
        }
    }

    private async syncCreateFrequency() {
        if (!this.deviceService.isOffline) {
            try {
                const frequency = await this.marketScanDataService.getCreateFrequency();
                if (frequency) {
                    this.authService.updateUserProps({ marketScanCreateFrequency: frequency });
                } else {
                    this.authService.updateUserProps({ marketScanCreateFrequency: CreateFrequency.MONTHLY });
                }
                const newDoc = {
                    user: this.authService.user,
                    lastModified: new Date().getTime()
                };
                await this.diskService.updateOrInsert(DB_KEY_PREFIXES.USER, doc => newDoc);

            } catch (error) {
                console.log("Error retreiving marketScanCreateFrequency ", error);
            }
        }
    }

    /**
     * Awaited, called as part of core sync alongside the contacts master-data fetch (see
     * MasterDataService stage2) so segmentation data is available as soon as contacts are.
     */
    public async syncCustomerSegmentationsOnly(loadFromDbOnly = false, forceFullSync = false, parentSpanId?: string): Promise<void> {
        this.customerSegmentationsSyncState$.next('syncing');
        try {
            await this.syncProfilerService.timeAsync(
                'customer_segmentations_sync', () => this.syncCustomerSegmentations(loadFromDbOnly, forceFullSync), parentSpanId);
            // Catch up the derived brand<->segment mapping now that fresh data actually landed —
            // loadAndMapMarketScanMasterDataFromDB may have run earlier (stage3's market-scan
            // master data) with whatever was in memory at the time.
            if (this.authService.hasFeatureAction(FeatureActionsMap.CUSTOMER_SCAN_TOOL)) {
                this.prepareAllFilterToTherapeuticArea();
            }
            this.customerSegmentationsSyncState$.next('ready');
        } catch (error) {
            console.error('[customer_segmentations_sync] sync failed', error);
            this.customerSegmentationsSyncState$.next('error');
            throw error;
        }
    }

    private async syncCustomerSegmentations(loadFromDbOnly = false, forceFullSync = false) {
        let offlineFallback: boolean = this.deviceService.isOffline || loadFromDbOnly;
        console.log(`[customer_segmentations_sync] syncCustomerSegmentations: offlineFallback=${offlineFallback} `
            + `(deviceOffline=${this.deviceService.isOffline}, loadFromDbOnly=${loadFromDbOnly}), forceFullSync=${forceFullSync}`);
        if (offlineFallback) {
            await this.loadCustomerSegmentationFromDB();
        } else {
            try {
                // Fetch/merge/persist of the raw indskr_productrating rows lives in
                // CustomerSegmentationOfflineService — shared with ContactOfflineService so both
                // build their views (grouped-by-segment here, grouped-by-contact there) from the
                // same single fetch instead of each syncing this entity independently.
                const rawRows = await this.customerSegmentationService.sync(forceFullSync);
                this.customerSegmentations = this.groupCustomerSegmentationRawRows(rawRows);
                console.log(`[customer_segmentations_sync] grouped ${rawRows.length} raw rows into ${this.customerSegmentations.length} segment groups`);

                await this.diskService.updateOrInsert(DB_KEY_PREFIXES.CUSTOMER_SEGMENTATION, () => ({ raw: this.customerSegmentations }))
                    .catch((err) =>
                        console.error("syncCustomerSegmentations: Error saving customer segmentations data to offline db!", err)
                    );
            } catch (error) {
                console.log("Error retreiving customer segmentations data ", error);
                throw error;
            }
        }
    }

    /**
     * Fire-and-forget adoption-ladder counterpart to syncCustomerSegmentationsOnly — called from
     * startLadderBackgroundSync. Stores the raw (ungrouped) row set directly; no consumer needs a
     * grouped-by-ladder view, so unlike segmentation there's no derived disk-persisted view here.
     */
    private async syncAdoptionLadderRatings(loadFromDbOnly = false, forceFullSync = false) {
        let offlineFallback: boolean = this.deviceService.isOffline || loadFromDbOnly;
        console.log(`[adoption_ladder_sync] syncAdoptionLadderRatings: offlineFallback=${offlineFallback} `
            + `(deviceOffline=${this.deviceService.isOffline}, loadFromDbOnly=${loadFromDbOnly}), forceFullSync=${forceFullSync}`);
        if (offlineFallback) {
            this.adoptionLadderRatings = await this.productRatingService.loadFromDB();
        } else {
            try {
                this.adoptionLadderRatings = await this.productRatingService.sync(forceFullSync);
                console.log(`[adoption_ladder_sync] ${this.adoptionLadderRatings.length} raw rows cached`);
            } catch (error) {
                console.log("Error retreiving adoption ladder ratings data ", error);
                throw error;
            }
        }
    }

    /** Last-known merged adoption-ladder raw-row set, synchronously. */
    public getAdoptionLadderRatings(): any[] {
        return this.adoptionLadderRatings;
    }

    /** Rebuilds the grouped-by-segment CustomerSegmentation[] view consumers read, from the full
     * raw-row set — must run over the complete set (not just a delta slice) since a delta fetch
     * only ever returns a partial slice of rows. Mirrors the grouping that used to live directly
     * inside MarketScanDataService.getCustomerSegmentations before it returned raw rows instead. */
    private groupCustomerSegmentationRawRows(rawRows: any[]): CustomerSegmentation[] {
        return _(rawRows)
            .map((item: any) => ({
                indskr_customersegmentationid: item.indskr_customersegmentationid,
                indskr_name: item.indskr_name,
                productId: item.productId,
                contactId: item.contactId,
                adoptionLadderId: item.adoptionLadderId,
                customerSegmentations: [] as CustomerSegmentation[]
            }))
            .groupBy("indskr_customersegmentationid")
            .map(value => {
                value[0].customerSegmentations = value.map(v => ({ ...v, customerSegmentations: [] as CustomerSegmentation[] }));
                return value[0];
            })
            .value();
    }

    private async loadCustomerSegmentationFromDB() {
        // Also restore the raw-row cache (via the shared service), not just the grouped view —
        // otherwise the next online delta sync (after a period offline/DB_ONLY) would have an
        // empty merge baseline and silently lose every row that isn't part of that one delta fetch.
        await this.customerSegmentationService.loadFromDB();
        await this.diskService.retrieve(DB_KEY_PREFIXES.CUSTOMER_SEGMENTATION).then((data) => {
            if (data && data['raw']) {
                this.customerSegmentations = data['raw'];
                this.customerSegmentations = this.sortListByFielName(this.customerSegmentations, 'indskr_name');
            }
            console.log(`Customer segments from disk : ${data && data['raw'] ? data['raw'].length : 0}`);
        });
    }

    public async syncMarketScanCategories(loadFromDbOnly = false) {
        let offlineFallback: boolean = this.deviceService.isOffline || loadFromDbOnly;
        if (offlineFallback) {
            await this.loadCategoriesFromDB();
        } else {
            let url: string = this.authService.userConfig.activeInstance.entryPointUrl + Endpoints.marketScan.GET_CATEGORIES;
            await this.marketScanDataService.getMarketScanCategories(url).then(async (data: Category[]) => {
                if (data) {
                    data.forEach((d: Category) => {
                      //backward compatabilty
                      if (!d.indskr_capturedatafor) {
                        d.indskr_capturedatafor = CaptureDataFor.CUSTOMERS;
                      }
                      d.measures.forEach(m=>{
                        if (!m.captureFor) {
                          m.captureFor = MeasureCaptureFor.CUSTOMERS_ACCOUNTS;
                        }
                      })
                      d.measures = this.sortListByFielName(d.measures, "measureName");
                      this.mapCatureData(d);
                    })
                    this.categories = data;
                    await this.diskService.updateOrInsert(DB_KEY_PREFIXES.MARKET_SCAN_CATEGORIES, doc => ({ raw: data, fetchEventsAttribute: this.fetchEventsAttribute, fetchCampaignsAttribute: this.fetchCampaignsAttribute })).catch((err) => console.error("syncMarketScanCategories: Error saving categories data to offline db!", err));
                }
            }).catch((err) => {
                console.error("syncMarketScanCategories: Error fetching categories!", err);
            });
        }
    }

    private mapCatureData(d: Category) {
        if (d.indskr_capturedata) {
            if (d.indskr_capturedata === CaptureData.EVENTS && !this.hasEvents) {
                this.hasEvents = true;
            }
            else if (d.indskr_capturedata === CaptureData.CAMPAIGN && !this.hasCampaigns) {
                this.hasCampaigns = true;
            }
            else if (d.indskr_capturedata === CaptureData.ACCOUNTS && !this.hasAccounts) {
              this.hasAccounts = true;
            }
        } else {
            d.indskr_capturedata = CaptureData.PRODUCT;
        }
    }

    private async loadCategoriesFromDB() {
        await this.diskService.retrieve(DB_KEY_PREFIXES.MARKET_SCAN_CATEGORIES).then((data) => {
            if (data) {
                if (data['raw']) {
                    this.categories = data['raw'];
                    this.categories.forEach(category => this.mapCatureData(category));
                    console.log(`Categories from disk : ${data['raw'].length}`);
                }
                this.fetchEventsAttribute = data['fetchEventsAttribute'];
                this.fetchCampaignsAttribute = data['fetchCampaignsAttribute'];
            }
        }).catch((err) => console.error("syncMarketScanCategories: Error saving categories data to offline db!", err));
    }

    private async syncMarketScans(dataRange: { from: string, to: string }, loadFromDbOnly = false) {
        let offlineFallback: boolean = this.deviceService.isOffline || loadFromDbOnly;
        if (offlineFallback) {
            await this.loadMarketScansFromDB();
        } else {
            const syncState = await this.diskService.getSyncState(DB_SYNC_STATE_KEYS.SYNC_MARKET_SCAN);
            const isInitialSync = !syncState || !syncState.lastUpdatedTime;
            const marketScanSyncInfo: EntitySyncInfo = {
                entityName: EntityNames.marketScan,
                totalFailed: 0,
                totalSynced: 0,
                errors: [],
                syncStatus: true
            };
            if (isInitialSync) {
                await this.diskService.deleteAllFromDbUsingAlldocsQuery(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_MARKET_SCAN);
            }
            let url: string = this.authService.userConfig.activeInstance.entryPointUrl + Endpoints.marketScan.GET_MARKET_SCANS
                .replace('{startDate}', dataRange.from)
                .replace('{endDate}', dataRange.to)
              .replace('{positionId}', this.authService.user.xPositionID);
            url = isInitialSync ? url : url + '&lastUpdatedTime=' + syncState.lastUpdatedTime;
            if (this.fetchCampaignsAttribute || this.fetchEventsAttribute) {
                url = url + '&captureData=events,campaigns';
            }
            await this.marketScanDataService.getMarketScans(url).then(
                async (data: MarketScan[]) => {
                    const newLastUpdatedTime = new Date().getTime().toString();
                    if (isInitialSync) {
                        await this.mapFullSyncedMarketScans(data, newLastUpdatedTime);
                    }
                    else {
                        await this.mapDeltaSyncedMarketScans(data, newLastUpdatedTime);
                    }
                    syncState.lastUpdatedTime = newLastUpdatedTime;
                    await this.diskService.updateSyncState(syncState);
                    if (Array.isArray(data)) {
                        marketScanSyncInfo.totalSynced = data.length;
                    }
                    this.deltaService.addEntitySyncInfo(marketScanSyncInfo);
                    console.log(`Sync status : ${JSON.stringify(syncState)} initial sync : ${isInitialSync}`);
                }).catch(err => {
                    console.error("Error occurred while fetching market scans..." + err)
                    this.deltaService.addSyncErrorToEntitySyncInfo(marketScanSyncInfo, this.authService.userConfig.activeInstance.entryPointUrl + Endpoints.marketScan.GET_MARKET_SCANS, err);
                    this.deltaService.addEntitySyncInfo(marketScanSyncInfo);
                })
        }
    }

    private async loadMarketScansFromDB() {
        await this.diskService.batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_MARKET_SCAN).then((data: MarketScan[]) => {
            const marketScans: MarketScan[] = [];
            //Filter cancelled market scans
            data.filter(data => data.statuscode != MarketScanStatusCode.Cancelled).forEach(d => {
                marketScans.push(this.getOnlyMetaData(d));
            });
            this.marketScans.next(marketScans);
            console.log(`Market scans from disk : ${marketScans ? marketScans.length : 0}`);
        });
        // For offline data tracking...
        await this.checkOfflineDataExists();
    }

    public async checkOfflineDataExists(): Promise<boolean> {
        const offlineMarketScans = this.marketScans.getValue().filter(marketScan => marketScan.pendingPushToDynamics);
        const offlineDataCount = offlineMarketScans ? offlineMarketScans.length : 0;
        this.diskService.setOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MARKET_SCANS, offlineDataCount);
        return offlineDataCount ? true : false;
    }

    private async mapDeltaSyncedMarketScans(rawData: MarketScan[], newLastUpdatedTime: string) {
        if (rawData && rawData.length > 0) {
            for (let data of rawData) {
                const key = DB_KEY_PREFIXES.MARKET_SCANS + data.indskr_externalid;
                if (!(data.statuscode === MarketScanStatusCode.Completed || data.statuscode === MarketScanStatusCode.Expired || data.statuscode === MarketScanStatusCode.PendingSync)) {
                    await this.diskService.remove(key);
                } else {
                    data._id = key;
                    data.lastUpdatedTime = newLastUpdatedTime;
                    if (this.getSelectedMarketScan() && this.getSelectedMarketScan().indskr_externalid === data.indskr_externalid) {
                        //Refresh details screen on delta sync
                        this.setCurrentMarketScan(data);
                        this.marketScanCopy = cloneDeep(data);
                    }
                    if (this.marketScans.getValue()) {
                        this.mapCategory(data);
                        this.upsertMarketScans(data);
                    }
                    await this.diskService.updateOrInsert(key, (doc) => { return data; });
                }
            }
        }
    }

    private async mapFullSyncedMarketScans(rawData: MarketScan[], newLastUpdatedTime: string) {
        rawData.forEach((data: MarketScan) => {
            data._id = DB_KEY_PREFIXES.MARKET_SCANS + data.indskr_externalid;
            data.lastUpdatedTime = newLastUpdatedTime;
        });
        await this.diskService.bulk(rawData);
    }

    public async mapFilteredDataToSelectedMarketScan(marketScan: MarketScan, type: FilterType) {
        const startTime = new Date().getTime();
        switch (type) {
            case FilterType.CUSTOMER_SEGMENTATION:
                this.mapFiltersForCustomerSegmentation(marketScan,false);
                break;
            case FilterType.PRODUCT:
              this.mapFiltersForProduct(marketScan);
              break;
            default:
                console.log("Unhandled case");

        }
        this.createUpdateMarketScanCustomerDataPayload(marketScan);
        await this.updateMarketScanInPouchDB(marketScan);
        this.setCurrentMarketScan(marketScan)
        const endTime = new Date().getTime();
        console.log("mapFilteredDataToSelectedMarketScan: " + (endTime - startTime) + "type: " + type);
    }

    private mapFiltersForProduct(marketScan: MarketScan) {
        if (marketScan.category.indskr_capturedatafor === CaptureDataFor.CUSTOMERS || (marketScan.category.indskr_capturedatafor === CaptureDataFor.CUSTOMERS_ACCOUNTS && marketScan.selectView == 'CUSTOMER_VIEW')) {
        let selectedCustomerSegments: Map<string, MarketScanCustomerSegment> = new Map<string, MarketScanCustomerSegment>();
        let selectedContacts: Map<string, MarketScanContact> = new Map<string, MarketScanContact>();
        let filteredProducts = marketScan.products.filter(pr => pr.action && pr.action != Action.DELETE);
        if (filteredProducts && filteredProducts.length) {
          filteredProducts.forEach(mProduct => {
            const index = this.brands.findIndex(p => p.ID === mProduct.productId && p['customerSegmentations']);
            if (index >= 0) {
              this.brands[index]['customerSegmentations'].forEach(cs => {
                if (!selectedCustomerSegments.has(cs.customerSegmentationId)) {
                  selectedCustomerSegments.set(cs.customerSegmentationId, new MarketScanCustomerSegment(cs.segmentName, cs.customerSegmentationId, cs.action));
                }
                cs['contacts'].forEach(c => {
                  if (!selectedContacts.has(c.contactId)) {
                    selectedContacts.set(c.contactId, new MarketScanContact(c.contact_fullName, c.contactId, c.action));
                  }
                });
              });
            }
          });
        }
        let selectedCustomerSegmentsList = Array.from(selectedCustomerSegments.values());
        let selectedContactsList = Array.from(selectedContacts.values())
        if (marketScan.indskr_marketscanid) {
          this.unMapReMapSegments(selectedCustomerSegmentsList, marketScan);
        }
        marketScan.customerSegmentations = selectedCustomerSegmentsList;
        marketScan.segmentedContacts = this.sortListByFielName(selectedContactsList, "contact_fullName");
      }
    }

    public mapFiltersForCustomerSegmentation(marketScan: MarketScan, resetSelectedContacts: boolean = true) {
        let selectedContacts: Map<string, MarketScanContact> = new Map<string, MarketScanContact>();
        let customerSegId = [];
        let filteredSegments = marketScan.customerSegmentations.filter(seg => seg.action && seg.action != Action.DELETE);
        if (filteredSegments && filteredSegments.length) {
            filteredSegments.map(cs => customerSegId.push(cs.customerSegmentationId));
            if (filteredSegments.length > 0)
                marketScan.products.forEach(mProduct => {
                    const index = this.brands.findIndex(p => p.ID === mProduct.productId && p['customerSegmentations']);
                    if (index >= 0) {
                        this.brands[index]['customerSegmentations'].filter(cs => customerSegId.includes(cs.customerSegmentationId)).map(cs => {
                            cs['contacts'].forEach(c => {
                                if (!selectedContacts.has(c.contactId)) {
                                    selectedContacts.set(c.contactId, new MarketScanContact(c.contact_fullName, c.contactId, c.action));
                                }
                            });
                        });
                    }
                });
        }
        let selectedContactsList = Array.from(selectedContacts.values());
        marketScan.segmentedContacts = this.sortListByFielName(selectedContactsList, "contact_fullName");
    }

    private unMapReMap<T extends { action?: Action }>(
        selectedEntities: T[],
        marketScan: MarketScan,
        collectionKey: 'products' | 'customerSegmentations' | 'contacts' | 'competitors' | 'accounts',
        idKey: keyof T,
        cascadeDeleteKey?: keyof MarketScanCustomerData,
        hasSequence?: boolean,
    ): void {
        if (isEmpty(marketScan.indskr_marketscanid)) return;
        const collection = marketScan[collectionKey] as unknown as T[];
        if (!selectedEntities.length) {
            selectedEntities.push(...collection);
            selectedEntities.forEach(entity => entity.action = Action.DELETE);
            return;
        }
        const alreadySelectedIds = map(collection.filter(entity => entity.action && entity.action !== Action.DELETE), idKey as string);
        const currentSelectedIds = map(selectedEntities, idKey as string);
        if (!alreadySelectedIds.length && every(collection, ['action', Action.DELETE])) {
            const ids = selectedEntities.map(entity => entity[idKey]);
            collection.forEach(entity => {
                // If removed and added back same entity, don't add it to selected list again
                if (entity.action === Action.DELETE && ids.includes(entity[idKey])) {
                    entity.action = Action.PATCH;
                    if (hasSequence) {
                        (entity as any).sequence = (selectedEntities.find(selected => selected[idKey] === entity[idKey]) as any)?.sequence;
                    }
                } else {
                    selectedEntities.push(entity);
                }
            });
        }
        const diffIds = differenceWith(alreadySelectedIds, currentSelectedIds, isEqual);
        if (diffIds && diffIds.length) {
            diffIds.forEach(diffId => {
                const deletableEntity = collection.find(entity => entity[idKey] == diffId);
                if (deletableEntity) {
                    deletableEntity.action = Action.DELETE;
                    selectedEntities.push(deletableEntity);
                }
            });
        } else {
            selectedEntities.forEach(entity => {
                entity.action = entity.action ? entity.action : Action.PATCH;
            });
        }
        // Add unique deletable objects missed by above logic —
        // scenario: collection has one object with action PATCH and another as DELETE
        const deletableEntities = collection.filter(entity => entity.action && entity.action === Action.DELETE);
        if (deletableEntities.length > 0) {
            const ids = selectedEntities.map(entity => entity[idKey]);
            deletableEntities.forEach(deletableEntity => {
                if (cascadeDeleteKey) {
                    const relatedData = marketScan.marketScanCustomerDatas.filter(
                        customerData => customerData[cascadeDeleteKey] === deletableEntity[idKey]
                    );
                    relatedData.forEach(item => { item.action = Action.DELETE; });
                }
                if (!ids.includes(deletableEntity[idKey])) {
                    selectedEntities.push(deletableEntity);
                }
            });
        }
    }

    unMapReMapProducts(selectedProducts: MarketScanProduct[], marketScan: MarketScan) {
        this.unMapReMap(selectedProducts, marketScan, 'products', 'productId');
    }

    unMapReMapSegments(selectedCustomerSegments: MarketScanCustomerSegment[], marketScan: MarketScan) {
        this.unMapReMap(selectedCustomerSegments, marketScan, 'customerSegmentations', 'customerSegmentationId');
    }

    unMapReMapContacts(selectedContacts: MarketScanContact[], marketScan: MarketScan) {
        this.unMapReMap(selectedContacts, marketScan, 'contacts', 'contactId', 'contactId', true);
    }

    unMapReMapCompetitors(selectedCompetitors: MarketScanCompetitor[], marketScan: MarketScan) {
        this.unMapReMap(selectedCompetitors, marketScan, 'competitors', 'competitorId', 'competitorId', true);
    }

    unMapReMapAccounts(selectedAccounts: MarketScanAccount[], marketScan: MarketScan) {
        this.unMapReMap(selectedAccounts, marketScan, 'accounts', 'accountId', 'accountId', true);
    }

    public async mapAccountsToMarketScan(marketScan: MarketScan, accounts: MarketScanAccount[]) {
      this.unMapReMapAccounts(accounts, marketScan);
      accounts = this.sortListByFielName(accounts, 'accountName');
      marketScan.accounts = accounts;
      this.createUpdateMarketScanCustomerDataPayload(marketScan);
      await this.updateMarketScanInPouchDB(marketScan);
      this.upsertMarketScans(marketScan);
      this.setCurrentMarketScan(marketScan);
    }

    public async mapContactsToMarketScan(marketScan: MarketScan, contacts: MarketScanContact[]) {
        contacts = this.sortListByFielName(contacts, 'contact_fullName');
        marketScan.contacts = contacts;
        if (marketScan.category.indskr_capturedata === CaptureData.ACCOUNTS && (isEmpty(marketScan.marketScanCustomerDatas) || marketScan.marketScanCustomerDatas.filter(columnData => columnData.action === Action.PATCH).length === 0)
        && (!isEmpty(marketScan.contacts) && marketScan.contacts.filter(contact => contact.action === Action.PATCH).length > 0)) {
          this.notificationService.notify(this.translate.instant("NO_DATA_TO_CAPTURE_CUSTOMER_SCAN", { captureData: this.translate.instant("ACCOUNTS").toLowerCase() }), 'Market Scan', 'top', ToastStyle.DANGER);
        }
        this.createUpdateMarketScanCustomerDataPayload(marketScan);
        await this.updateMarketScanInPouchDB(marketScan);
        this.setCurrentMarketScan(marketScan);
    }

    public async mapCompetitorToMarketScan(marketScan: MarketScan, competitors: MarketScanCompetitor[]) {
      competitors = this.sortListByFielName(competitors, 'competitorName');
      marketScan.competitors = competitors;
      this.createUpdateMarketScanCustomerDataPayload(marketScan);
      await this.updateMarketScanInPouchDB(marketScan);
      this.setCurrentMarketScan(marketScan);
    }

    public displayText(any, fieldName: string): string {
        if (isEmpty(any)) {
            return "";
        }
        else {
            const filteredItems = any.filter((v) => v.action !== Action.DELETE);
            if (filteredItems.length) {
                any = filteredItems;
            } else {
                return "";
            }

            any = this.sortListByFielName(any, fieldName);;
            if (fieldName === 'contact_fullName' || fieldName === 'accountName') {
                return any.length > 1 ? (any[0][fieldName].concat(this.getInavtiveText(any[0])).concat(' +').concat(any.length - 1)) : (any[0][fieldName] + this.getInavtiveText(any[0]));
            }
            return any.length > 1 ? (any[0][fieldName].concat(' +').concat(any.length - 1)) : any[0][fieldName];
        }
    }

    getInavtiveText(obj: any) {
        return obj['isActive'] === false ? ` (${this.translate.instant('INACTIVE')})` : '';
    }

    public async createMarketScan(marketScan: MarketScan) {
        if (this.deviceService.isOffline) {
            //Keep offline count
            if (!marketScan.pendingPushToDynamics)
                this.diskService.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MARKET_SCANS, 1);
            marketScan.pendingPushToDynamics = true;
            this.marketScanCopy = cloneDeep(marketScan);
        } else {
            marketScan.statuscode = MarketScanStatusCode.Completed;
            this.upsertMarketScans(marketScan);
            const payload = this._getMarketScanServicePayload(marketScan);
            await this.marketScanDataService.createMarketScan(payload).then(async (res) => {
                if (res && res['indskr_marketscanid']) {
                    marketScan.pendingPushToDynamics = false;
                    marketScan.lastUpdatedTime = new Date().getTime().toString();
                    marketScan.indskr_marketscanid = res['indskr_marketscanid'];
                    this.marketScanCopy = cloneDeep(marketScan);
                }
            }).catch(err => {
                console.error("Failed to save marketscan due to: ", err);
                marketScan.statuscode = MarketScanStatusCode.PendingSync;
                marketScan.pendingPushToDynamics = true;
                this.diskService.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MARKET_SCANS, 1);
            })
        }
        this.createUpdateMarketScanCustomerDataPayload(marketScan);
        this.upsertMarketScans(marketScan);
        await this.updateMarketScanInPouchDB(marketScan);
        marketScan.isModified = false;
        this.setCurrentMarketScan(marketScan);
    }

    async scrapMarketScan(marketScan: MarketScan) {
        const selectedMarketScanId = marketScan.indskr_marketscanid;
        if (this.deviceService.isOffline) {
            if (selectedMarketScanId) {
                marketScan.statuscode = MarketScanStatusCode.Cancelled;
                if (!marketScan.pendingPushToDynamics) {
                    await this.diskService.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MARKET_SCANS, 1);
                    marketScan.pendingPushToDynamics = true;
                }
                //Service expects only id and status for scrapping record
                const scrapRecord = {
                    indskr_externalid: marketScan.indskr_externalid,
                    indskr_marketscanid: marketScan.indskr_marketscanid,
                    statecode: MarketScanStateCode.InActive,
                    statuscode: MarketScanStatusCode.Cancelled,
                    pendingPushToDynamics: true
                };
                return this.updateMarketScanInPouchDB(<MarketScan>scrapRecord).then(() => {
                    this.removeMarketScanFromUI(marketScan.indskr_externalid);
                }).catch(() => {
                    marketScan.statuscode = MarketScanStatusCode.Completed;
                });
            } else {
                //If offline created, offline scrap
                if (marketScan.pendingPushToDynamics) {
                    await this.diskService.subtractOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MARKET_SCANS, 1);
                }
                return this.scrapMarketScanInPouchDB(marketScan.indskr_externalid);
            }
        } else {
            if (selectedMarketScanId) {
                await this.marketScanDataService.scrapMarketScan(selectedMarketScanId);
            }
            return this.scrapMarketScanInPouchDB(marketScan.indskr_externalid);
        }
    }

    public async updateMarketScan(marketScan: MarketScan) {
      if (this.deviceService.isOffline) {
        if (!marketScan.pendingPushToDynamics)
          this.diskService.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MARKET_SCANS, 1);
        marketScan.pendingPushToDynamics = true;
        this.marketScanCopy = cloneDeep(marketScan);
      } else {
        marketScan.statuscode = MarketScanStatusCode.Completed;
        this.upsertMarketScans(marketScan);
        const onlyChangedData = this._getMarketScanServicePayload(marketScan);
        await this.marketScanDataService.updateMarketScan(onlyChangedData).then(async (res) => {
          if (res && res['indskr_marketscanid']) {
            marketScan.pendingPushToDynamics = false;
            marketScan.lastUpdatedTime = new Date().getTime().toString();
            marketScan.indskr_marketscanid = res['indskr_marketscanid'];
            this.removeDELETEActionObjs(marketScan);
            this.marketScanCopy = cloneDeep(marketScan);
          }
        }).catch(err => {
          console.error("Failed to update marketscan due to: ", err);
          marketScan.statuscode = MarketScanStatusCode.PendingSync;
          marketScan.pendingPushToDynamics = true;
          this.diskService.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MARKET_SCANS, 1);
        })
      }
      this.createUpdateMarketScanCustomerDataPayload(marketScan);
      this.upsertMarketScans(marketScan);
      await this.updateMarketScanInPouchDB(marketScan);
      marketScan.isModified = false;
      this.setCurrentMarketScan(marketScan);
    }

    private prepareRequestBodyWithOnlyChangedDataForUpdate(marketScan: MarketScan): MarketScan {
        if (!this.marketScanCopy || this.marketScanCopy.indskr_externalid != marketScan.indskr_externalid) {
            //Just in case, copy doesn't have same market scan
            return marketScan;
        } else if (this.marketScanCopy.statuscode === MarketScanStatusCode.Active && marketScan.statuscode === MarketScanStatusCode.Completed) {
            return marketScan; //To handle app kill scenario, db will have latest data
        } else if (marketScan.pendingPushToDynamics) {
            return marketScan;
        }
        //update request body should have only changed related entities data along with meta data, as it can make performance hit
        let onlyChangedData: MarketScan = new MarketScan(marketScan);
        onlyChangedData.therapeuticAreas.push(...differenceWith(marketScan.therapeuticAreas, this.marketScanCopy.therapeuticAreas, isEqual));
        onlyChangedData.products.push(...differenceWith(marketScan.products, this.marketScanCopy.products, isEqual));
        onlyChangedData.customerSegmentations.push(...differenceWith(marketScan.customerSegmentations, this.marketScanCopy.customerSegmentations, isEqual));
        onlyChangedData.contacts.push(...differenceWith(marketScan.contacts, this.marketScanCopy.contacts, isEqual));
        onlyChangedData.accounts.push(...differenceWith(marketScan.accounts, this.marketScanCopy.accounts, isEqual));
        const customerData: MarketScanCustomerData[] = differenceWith(marketScan.marketScanCustomerDatas, this.marketScanCopy.marketScanCustomerDatas, isEqual);
        let onlyChangedCustomerData: MarketScanCustomerData[] = [];
        onlyChangedData.marketScanCustomerDatas.push(...onlyChangedCustomerData);
        delete onlyChangedData.therapeuticAreas;
        delete onlyChangedData.products;
        delete onlyChangedData.contacts;
        delete onlyChangedData.accounts;
        return onlyChangedData;
    }

    removeDELETEActionObjs(marketScan: MarketScan) {
      if (marketScan.category) {
        remove(marketScan.category.measures, m => m.action === Action.DELETE);
      }
      remove(marketScan.therapeuticAreas, ta => ta.action === Action.DELETE);
      remove(marketScan.products, p => p.action === Action.DELETE);
      remove(marketScan.customerSegmentations, cs => cs.action === Action.DELETE);
      remove(marketScan.contacts, c => c.action === Action.DELETE);
      remove(marketScan.accounts, c => c.action === Action.DELETE);
      if (marketScan.marketScanCustomerDatas) {
        remove(marketScan.marketScanCustomerDatas, c => c.action === Action.DELETE);
        marketScan.marketScanCustomerDatas.forEach(c => {
          c.action = null;
        });
      }
    }

    async scrapMarketScanInPouchDB(id: string) {
        await this.diskService.remove(DB_KEY_PREFIXES.MARKET_SCANS + id).then(() => {
            this.removeMarketScanFromUI(id);
        });
    }

    removeMarketScanFromUI(id: string) {
        let marketScans: MarketScan[] = this.marketScans.getValue();
        const index = marketScans.findIndex((ms) => ms.indskr_externalid === id);
        if (index >= 0) {
            marketScans.splice(index, 1);
            this.marketScans.next(marketScans);
        }
    }

    guidUtility() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            let r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    public getStatusText(statuscode: number) {
        let statusText = '';
        switch (statuscode) {
            case MarketScanStatusCode.Active:
                statusText = this.translate.instant('OPEN');
                break;
            case MarketScanStatusCode.Completed:
                statusText = this.translate.instant('COMPLETED');
                break;
            case MarketScanStatusCode.PendingSync:
                statusText = this.translate.instant('PENDING_SYNC');
                break;
            case MarketScanStatusCode.Cancelled:
                statusText = this.translate.instant('CANCELLED');
                break;
            case MarketScanStatusCode.Expired:
                statusText = this.translate.instant('COMPLETED');
                break;
            default:
                statusText = this.translate.instant('OPEN');
        }
        return statusText;
    }

    public async getMarketScanDeltaById(marketScanId: string) {
        const startTime = new Date().getTime();
        await this.revertStatus();
        const index = this.marketScans.getValue().findIndex(mS => mS.indskr_externalid === marketScanId);
        let marketScan: MarketScan = null;
        let fetchFromDB: boolean = true;
        if (index >= 0) {
            marketScan = cloneDeep(this.marketScans.getValue()[index]);
            if (!this.deviceService.isOffline && marketScan.indskr_marketscanid && !marketScan.pendingPushToDynamics && !marketScan.isModified) {
                let url = this.authService.userConfig.activeInstance.entryPointUrl + Endpoints.marketScan.GET_DELTA_MARKET_SCAN_BY_ID;
                url = url.replace('{marketScanId}', marketScan.indskr_marketscanid).replace('{lastUpdatedTime}', marketScan.lastUpdatedTime?marketScan.lastUpdatedTime:'0');
                if (this.fetchCampaignsAttribute || this.fetchEventsAttribute) {
                    url = url + '&captureData=events,campaigns';
                }
                const serviceStartTime = new Date().getTime();
                await this.marketScanDataService.getMarketScanDeltaById(url).then(async (data: MarketScan) => {
                    if (data) {
                        data = new MarketScan(data);
                        const endTime = new Date().getTime();
                        console.log("Time taken to service getMarketScanDeltaById: " + (endTime - serviceStartTime));
                        fetchFromDB = false;
                        data.lastUpdatedTime = new Date().getTime().toString();
                        await this.diskService.upsertMarketScan(data);
                        this.upsertMarketScans(data);
                        marketScan = data;
                    }
                }).catch(er => {
                    console.error("Error occurred while fetching delta market scan by id", er);
                });
            }
            if (fetchFromDB) {
                const key = DB_KEY_PREFIXES.MARKET_SCANS + marketScanId;
                marketScan = await this.diskService.retrieve(key);
                marketScan = new MarketScan(marketScan);
                if(!marketScan){
                  const newkey = DB_KEY_PREFIXES.MARKET_SCANS + marketScan.indskr_marketscanid;
                  marketScan = await this.diskService.retrieve(newkey);
                }
                if (marketScan?.statuscode === MarketScanStatusCode.Active) {
                    if (marketScan.pendingPushToDynamics || (marketScan.indskr_marketscanid && marketScan.isModified)) {
                        //For appkill, Incase value has edited but not saved
                        marketScan.statuscode = MarketScanStatusCode.PendingSync;
                        if ((marketScan.indskr_marketscanid && marketScan.isModified) && !marketScan.pendingPushToDynamics) {
                            marketScan.pendingPushToDynamics = true;
                            this.diskService.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MARKET_SCANS, 1);
                            await this.diskService.upsertMarketScan(marketScan); // to triggere sync
                        }
                    } else if (marketScan.indskr_marketscanid && !marketScan.isModified) {
                        //For appkill, Incase only status is changed but not modified
                        marketScan.statuscode = MarketScanStatusCode.Completed;
                    }
                }

            }
            this.mapCategory(marketScan);
            this.initializeLinkedEntities(marketScan);
            this.createUpdateMarketScanCustomerDataPayload(marketScan);
            this.upsertMarketScans(marketScan);
            if (marketScan.statuscode === MarketScanStatusCode.Active) {
                await this.checkForInActiveEntitites(marketScan);
            }
        }
        if (this.isUnderAllowedDurationForModification(marketScan.indskr_date)) {
            this.setInactiveFlagForContacts(marketScan);
            this.setInactiveFlagForAccounts(marketScan);
        }
        const endTime = new Date().getTime();
        console.log("Time taken to getMarketScanDeltaById: " + (endTime - startTime));
        return marketScan;
    }

    private async revertStatus() {
        const marketScan = this.getSelectedMarketScan();
        if (marketScan && this.marketScanCopy && marketScan.indskr_externalid === this.marketScanCopy.indskr_externalid) {
            //For already synced records
            if (marketScan.indskr_marketscanid) {
                if (marketScan.pendingPushToDynamics && marketScan.statuscode !== MarketScanStatusCode.PendingSync) {
                    if (!marketScan.isModified) {
                        marketScan.statuscode = MarketScanStatusCode.PendingSync;
                        this.upsertMarketScans(marketScan);
                        await this.updateMarketScanInPouchDB(marketScan);
                    } else if (this.marketScanCopy.statuscode === MarketScanStatusCode.PendingSync) {
                        this.marketScanCopy.pendingPushToDynamics = marketScan.pendingPushToDynamics;
                        this.upsertMarketScans(this.marketScanCopy);
                        await this.updateMarketScanInPouchDB(this.marketScanCopy);
                    }
                } else if (marketScan.statuscode === MarketScanStatusCode.Active && this.marketScanCopy.statuscode === MarketScanStatusCode.Completed) {
                    this.upsertMarketScans(this.marketScanCopy);
                    await this.updateMarketScanInPouchDB(this.marketScanCopy);
                }
            } else if (marketScan.statuscode === MarketScanStatusCode.Active && this.marketScanCopy.statuscode === MarketScanStatusCode.PendingSync) {
                //For offline, not synced records
                this.upsertMarketScans(this.marketScanCopy);
                await this.updateMarketScanInPouchDB(this.marketScanCopy);
            }
        }
    }

    private mapCategory(marketScan: MarketScan) {
        if (marketScan.category) {
            const index = this.categories.findIndex(c => c.indskr_assessmentcategoryid === marketScan.category.indskr_assessmentcategoryid);
            if (index === -1) {
              if (!marketScan.category.indskr_name.includes(" (" + this.translate.instant('INACTIVE') + ")"))
                marketScan.category.indskr_name += " (" + this.translate.instant('INACTIVE') + ")";
            } else {
              marketScan.category.indskr_name = this.categories[index].indskr_name;
            }
        }else if (marketScan.categoryId) {
          const index = this.categories.findIndex(c => c.indskr_assessmentcategoryid === marketScan.categoryId);
          if (index === -1) {
            if (!marketScan.category.indskr_name.includes(" (" + this.translate.instant('INACTIVE') + ")"))
              marketScan.category.indskr_name += " (" + this.translate.instant('INACTIVE') + ")";
          } else {
            marketScan.category = this.categories[index];
          }
        }  else {
            const validCategoryDataIndex = this.getValidCategory(marketScan);
            if (validCategoryDataIndex && validCategoryDataIndex.cDataIndex >= 0) {
                const validCustomerData: MarketScanCustomerData = marketScan.marketScanCustomerDatas[validCategoryDataIndex.cDataIndex];
                const categoryId = validCustomerData.indskr_assessmentcategoryid;
                let categoryName = validCustomerData.categoryName;
                const captureDataFor = validCustomerData.indskr_capturedatafor ? validCustomerData.indskr_capturedatafor : CaptureDataFor.CUSTOMERS;
                if (categoryId) {
                    const index = this.categories.findIndex(c => c.indskr_assessmentcategoryid === validCustomerData.indskr_assessmentcategoryid);
                    const measures: Measure[] = [];
                    if (index === -1) {
                      if (!categoryName.includes(" (" + this.translate.instant('INACTIVE') + ")"))
                        categoryName += " (" + this.translate.instant('INACTIVE') + ")";
                    } else {
                      categoryName = this.categories[index].indskr_name;
                    }
                    unionBy(marketScan.marketScanCustomerDatas, d => d.measureId).forEach(d => {
                        const foundMeasure = this.categories[index].measures.find(m=> m.measureId == d.measureId);
                        const measureCaptureFor = foundMeasure.captureFor;
                        const measureType = foundMeasure.measureType
                        const measure = new Measure(d.measureName, d.measureId, measureType, d.action ? d.action : Action.PATCH, measureCaptureFor);
                        measures.push(measure);
                    });
                    marketScan.category = new Category(categoryId, categoryName, validCustomerData.indskr_capturedata, captureDataFor, measures);
                }
            }
        }
    }

    private getValidCategory(marketScan: MarketScan) {
        //get first category with action PATCH, in case of offline it can customer data can contain previously selected category data with action deleted
        let cDataIndex: number = -1;
        if (!isEmpty(marketScan.marketScanCustomerDatas)) {
          cDataIndex = marketScan.marketScanCustomerDatas.findIndex(d => d.action != Action.DELETE);
        }
        return { cDataIndex: cDataIndex };
    }

    private initializeLinkedEntities(data: MarketScan) {
        if(!data) return;
        if(!isEmpty(data.marketScanCustomerDatas) && isEmpty(data.products)){
          let products:Array<MarketScanProduct> = [];
          let accounts:Array<MarketScanAccount> = [];
          let contacts:Array<MarketScanContact> = [];
          let competitors:Array<MarketScanCompetitor> = [];

          const accountcontactidpair :Array <{
            contactid:string,
            accountid:string,
          }> = [];
          data.marketScanCustomerDatas.forEach(item => {
            if(item.action != Action.DELETE){
              if(item.productId && !products.some(p => p.productId == item.productId)){
                let foundBrand;
                if(data.category && data.category.indskr_capturedatafor == CaptureDataFor.COMPETITOR){
                  foundBrand = this.surgeryOrderDataService.productHierarchies && this.surgeryOrderDataService.productHierarchies.find(({surgeryId}) => surgeryId === item.productId);
                  if(foundBrand) products.push(new MarketScanProduct(foundBrand.surgeryId, foundBrand.surgeryName,false, Action.PATCH));
                } else {
                  foundBrand = this.brands.find(b=> b.ID == item.productId);
                  if(foundBrand){
                    products.push(new MarketScanProduct(foundBrand.ID, foundBrand.name,foundBrand.isCompetitorProduct == 'true', Action.PATCH));
                  }
                }
              }
              if(data.category && data.category.indskr_capturedatafor == CaptureDataFor.CUSTOMERS_ACCOUNTS){
                let idx = accountcontactidpair.findIndex(m=> m.accountid == item.accountId && m.contactid == item.contactId);
                if(idx < 0){
                  accountcontactidpair.push({accountid:item.accountId,contactid:item.contactId});
                }
              }else{
                if(item.accountId && !accounts.some(a => a.accountId == item.accountId)){
                  let foundAccount = this.accountService.getAccountById(item.accountId);
                  if(foundAccount){
                    accounts.push(new MarketScanAccount(foundAccount.accountName, foundAccount.id, Action.PATCH));
                  }
                }
                if(item.contactId && !contacts.some(c => c.contactId == item.contactId)){
                  let foundContact = this.contactService.getContactByID(item.contactId);
                  if(foundContact){
                    contacts.push(new MarketScanContact(foundContact.fullName,foundContact.ID, Action.PATCH));
                  }
                }
                if(item.competitorId && !competitors.some(c => c.competitorId == item.competitorId)){
                  let foundCompetitor = this.opportunityService.opportunityCompetitors.find(({id}) => item.competitorId == id);
                  if(foundCompetitor){
                    competitors.push(new MarketScanCompetitor(foundCompetitor.name, foundCompetitor.id,Action.PATCH));
                  }
                }
              }
            }
          })
          if(data.category && data.category.indskr_capturedatafor == CaptureDataFor.CUSTOMERS_ACCOUNTS){
            if(data.selectView && data.selectView == 'CUSTOMER_VIEW'){
              accountcontactidpair.forEach(pair => {
                if(pair.contactid){
                  let idx = contacts.findIndex(c => c.contactId == pair.contactid)
                  if(idx >= 0){
                    if(pair.accountid){
                      let newIdx  = contacts[idx].affiliatedAccounts.findIndex(aa=> aa.accountId == pair.accountid)
                      if(newIdx < 0){
                        let foundAccount = this.accountService.getAccountById(pair.accountid);
                        if(foundAccount){
                          contacts[idx].affiliatedAccounts.push(new MarketScanAccount(foundAccount.accountName, foundAccount.id, Action.PATCH));
                        }
                      }
                    }
                  }else{
                    let foundContact = this.contactService.getContactByID(pair.contactid);
                    let msCon = new MarketScanContact(foundContact.fullName,foundContact.ID, Action.PATCH);
                    if(pair.accountid){
                      let foundAccount = this.accountService.getAccountById(pair.accountid);
                      if(foundAccount){
                        msCon.affiliatedAccounts.push(new MarketScanAccount(foundAccount.accountName, foundAccount.id, Action.PATCH));
                      }
                    }
                    contacts.push(msCon);
                  }
                }
              })
            }else{
              data.selectView = 'ACCOUNT_VIEW';
              accountcontactidpair.forEach(pair => {
                if(pair.accountid){
                  let idx = accounts.findIndex(c => c.accountId == pair.accountid)
                  if(idx >= 0){
                    if(pair.contactid){
                      let newIdx  = accounts[idx].affiliatedContacts.findIndex(aa=> aa.contactId == pair.contactid)
                      if(newIdx < 0){
                        let foundContact = this.contactService.getContactByID(pair.contactid);
                        if(foundContact){
                          accounts[idx].affiliatedContacts.push(new MarketScanContact(foundContact.fullName, foundContact.ID, Action.PATCH));
                        }
                      }
                    }
                  }else{
                    let foundAccount = this.accountService.getAccountById(pair.accountid);
                    let msAcc = new MarketScanAccount(foundAccount.accountName,foundAccount.id, Action.PATCH);
                    if(pair.contactid){
                      let foundContact = this.contactService.getContactByID(pair.contactid);
                      if(foundContact){
                        msAcc.affiliatedContacts.push(new MarketScanContact(foundContact.fullName, foundContact.ID, Action.PATCH));
                      }
                    }
                    accounts.push(msAcc);
                  }
                }
              })
            }
          }
          data.products = products;
          data.accounts = accounts;
          data.contacts = contacts;
          data.competitors = competitors;
        }
    }

    public async checkForInActiveEntitites(marketScan: MarketScan) {
        let foundInActive = false;
        if (this.isUnderAllowedDurationForModification(marketScan.indskr_date) && this.authService.user.systemUserID === marketScan.ownerId) {
            foundInActive = await this.removeInActiveEntities(marketScan);
            if (foundInActive) {
                this.notificationService.notify(this.translate.instant("REMOVED_DEACTIVATED").replace("{{activityType}}", String(this.translate.instant("CUSTOMER_SCAN")).toLowerCase()), 'Market Scan Detail', 'top', ToastStyle.INFO);
                if (marketScan.indskr_marketscanid) {
                    //For removing deactivated data, to enable update btn
                    marketScan.isModified = true;
                }
                await this.updateMarketScanInPouchDB(marketScan);
            }
        }
        return foundInActive;
    }

    private async removeInActiveEntities(marketScan: MarketScan) {
        let foundInActiveDetails: boolean = false;
        foundInActiveDetails = this.removeInActiveCategory(marketScan, foundInActiveDetails);
        if (!foundInActiveDetails)
            foundInActiveDetails = marketScan.category.indskr_capturedatafor == CaptureDataFor.COMPETITOR ? await this.removeInActiveProcedures(marketScan, foundInActiveDetails) :  await this.removeInActiveProducts(marketScan, foundInActiveDetails);
        if (!foundInActiveDetails)
            foundInActiveDetails = await this.removeInActiveCustomerSegmenations(marketScan, foundInActiveDetails);
        if (foundInActiveDetails) {
            this.removeInActiveMarketCustomerData(marketScan);
        }
        return foundInActiveDetails;
    }

    removeInActiveCategory(marketScan: MarketScan, foundInActiveDetails: boolean) {
        if (!isEmpty(marketScan.category)) {
            const categoryId = marketScan.category.indskr_assessmentcategoryid;
            let categoryName = marketScan.category.indskr_name;
            if (categoryId) {
                const index = this.categories.findIndex(c => c.indskr_assessmentcategoryid === marketScan.category.indskr_assessmentcategoryid);
                if (index === -1 && !categoryName.includes(" (" + this.translate.instant('INACTIVE') + ")"))
                    categoryName += " (" + this.translate.instant('INACTIVE') + ")";
                let measures: Measure[] = [];
                if (!isEmpty(marketScan.marketScanCustomerDatas)) {
                    const validCategoryDataIndex = this.getValidCategory(marketScan);
                    if (validCategoryDataIndex && validCategoryDataIndex.cDataIndex >= 0) {
                        const validCustomerData: MarketScanCustomerData = marketScan.marketScanCustomerDatas[validCategoryDataIndex.cDataIndex];
                        unionBy(marketScan.marketScanCustomerDatas, d => d.measureId).forEach(d => {
                            const foundMeasure = this.categories[index].measures.find(m=> m.measureId == d.measureId);
                            const measureCaptureFor = foundMeasure.captureFor;
                            const measureType = foundMeasure.measureType;
                            measures.push(new Measure(d.measureName, d.measureId, measureType, d.action ? d.action : Action.PATCH, measureCaptureFor));
                        });
                    }
                } else {
                    measures = marketScan.category.measures;
                }
                if (index >= 0) {
                    categoryName = this.categories[index].indskr_name;
                }
                if (index === -1) {
                    marketScan.category = new Category(categoryId, categoryName, marketScan.category.indskr_capturedata, marketScan.category.indskr_capturedatafor, measures);
                } else {
                    //Remove inactive measures
                    foundInActiveDetails = this.removeInActiveMeasures(index, marketScan, foundInActiveDetails, measures);
                    marketScan.category = new Category(categoryId, categoryName, marketScan.category.indskr_capturedata, marketScan.category.indskr_capturedatafor, []);
                    if (marketScan.indskr_marketscanid) {
                        marketScan.category.measures = measures;
                    } else {
                        marketScan.category.measures = measures.filter(m => m.action !== Action.DELETE);
                    }
                }
            }
        }
        return foundInActiveDetails;
    }

    private removeInActiveMeasures(index: number, marketScan: MarketScan, foundInActiveDetails: boolean, measures: Measure[]) {
        if (index > -1) {
            if (isEmpty(marketScan.marketScanCustomerDatas))
                return foundInActiveDetails;
            const measureIds = this.categories[index].measures.map(measure => measure.measureId);

            marketScan.marketScanCustomerDatas.forEach(cData => {
                if (!measureIds.includes(cData.measureId)) {
                  foundInActiveDetails = true;
                  if (marketScan.indskr_marketscanid) {
                    cData.action = Action.DELETE;
                  }
                  const mIndex = measures.findIndex(m => m.measureId === cData.measureId);
                  if (mIndex > -1)
                  measures[mIndex].action = Action.DELETE;
                }
            })
            return foundInActiveDetails;
        }
    }

    private async removeInActiveProducts(marketScan: MarketScan, foundInActiveDetails: boolean) {
        const availableProducts: string[] = this.brands.map(p => p.ID);
        if (!marketScan.indskr_marketscanid) {
            //Not saved in dynamics, remove TAs from list
            const inActiveProducts: MarketScanProduct[] = marketScan.products.filter(p => !availableProducts.includes(p.productId));
            if (inActiveProducts.length) {
                foundInActiveDetails = true;
                marketScan.products = difference(marketScan.products, inActiveProducts);
            }
        }
        else {
            //Saved TA in dynamics, add action as DELETE
            marketScan.products.filter(p => !availableProducts.includes(p.productId)).forEach(p => {
                foundInActiveDetails = true;
                p.action = Action.DELETE;
            });
        }
        if (foundInActiveDetails) {
            this.mapFiltersForProduct(marketScan);
        }
        return foundInActiveDetails;
    }

    private async removeInActiveProcedures(marketScan: MarketScan, foundInActiveDetails: boolean) {
      const availableProducts: string[] = this.surgeryOrderDataService.productHierarchies.map(p => p.surgeryId);
      if (!marketScan.indskr_marketscanid) {
          //Not saved in dynamics, remove TAs from list
          const inActiveProducts: MarketScanProduct[] = marketScan.products.filter(p => !availableProducts.includes(p.productId));
          if (inActiveProducts.length) {
              foundInActiveDetails = true;
              marketScan.products = difference(marketScan.products, inActiveProducts);
          }
      }
      else {
          //Saved TA in dynamics, add action as DELETE
          marketScan.products.filter(p => !availableProducts.includes(p.productId)).forEach(p => {
              foundInActiveDetails = true;
              p.action = Action.DELETE;
          });
      }
      if (foundInActiveDetails) {
          this.mapFiltersForProduct(marketScan);
      }
      return foundInActiveDetails;
  }

    private removeInActiveMarketCustomerData(marketScan: MarketScan) {
        const inActiveProductIds: string[] = marketScan.products.filter(p => p.action === Action.DELETE).map(p => p.productId);
        const activeEventIds: string[] = [];
        const activeCampaignIds: string[] = [];
        let activeAccountIds: string[] = [];
        if (marketScan.category.indskr_capturedata != CaptureData.PRODUCT && !isEmpty(inActiveProductIds)) {
            switch (marketScan.category.indskr_capturedata) {
                case CaptureData.EVENTS:
                    for (let productId in this.productToEventsMap) {
                        activeEventIds.push(...this.productToEventsMap[productId].map(ei => ei.msevtmgt_eventid));
                    }
                    break;
                case CaptureData.CAMPAIGN:
                    for (let productId in this.productToCampaignsMap) {
                        activeCampaignIds.push(...this.productToCampaignsMap[productId].map(ei => ei.msdyncrm_customerjourneyid));
                    }
                    break;
                default:
                    console.log("Unhandled case");
            }
        }
        const inActiveContactIds: string[] = marketScan.contacts.filter(c => c.action === Action.DELETE).map(c => c.contactId);
        if (marketScan.indskr_marketscanid && (inActiveContactIds.length || inActiveProductIds.length)) {
            if (marketScan.category.indskr_capturedata == CaptureData.ACCOUNTS && !isEmpty(inActiveContactIds)) {
              activeAccountIds.push(...this.contactToAccounts.map(a => a.accountId));
              activeAccountIds = uniqWith(activeAccountIds, isEqual);
            }
            marketScan.marketScanCustomerDatas.forEach(record => {
                if (inActiveContactIds.includes(record.contactId)) {
                    //If contact is removed, all rows of that contact should be deleted
                    record.action = Action.DELETE;
                }
                        switch (marketScan.category.indskr_capturedata) {
                            case CaptureData.EVENTS:
                                //If product is removed, all columns of that product should be deleted
                                if (!activeEventIds.includes(record.indskr_eventid)) {
                                    record.action = Action.DELETE;
                                }
                                break;
                            case CaptureData.CAMPAIGN:
                                //If product is removed, all columns of that product should be deleted
                                if (!activeCampaignIds.includes(record.indskr_campaignid)) {
                                    record.action = Action.DELETE;
                                }
                                break;
                          case CaptureData.ACCOUNTS:
                              //If product is removed, all columns of that product should be deleted
                              if (!activeAccountIds.includes(record.accountId)) {
                                record.action = Action.DELETE;
                              }
                              break;
                            default:
                                //If product is removed, all columns of that product should be deleted
                                if (inActiveProductIds.includes(record.productId)) {
                                    record.action = Action.DELETE;
                                }
                        }
            });
        }
    }

    private async removeInActiveCustomerSegmenations(marketScan: MarketScan, foundInActiveDetails: boolean) {
        const availableCSs: string[] = this.customerSegmentations.map(cs => cs.indskr_customersegmentationid);
        if (!marketScan.indskr_marketscanid) {
            const inActiveCSs: MarketScanCustomerSegment[] = marketScan.customerSegmentations.filter(cs => !availableCSs.includes(cs.customerSegmentationId));
            if (inActiveCSs.length) {
                foundInActiveDetails = true;
                marketScan.customerSegmentations = difference(marketScan.customerSegmentations, inActiveCSs);
            }
        }
        else {
            marketScan.customerSegmentations.filter(cs => !availableCSs.includes(cs.customerSegmentationId)).forEach(cs => {
                foundInActiveDetails = true;
                cs.action = Action.DELETE;
            });
        }
        if (foundInActiveDetails) {
            this.mapFiltersForCustomerSegmentation(marketScan,false);
        }
        return foundInActiveDetails;
    }

    public async purgeData(maxEndDateUnixTimestamp: number) {
        let option = {
            selector: {
                '_id': {
                    $gte: DB_KEY_PREFIXES.MARKET_SCANS,
                    $lte: DB_KEY_PREFIXES.MARKET_SCANS + PREFIX_SEARCH_ENDKEY_UNICODE
                },
                'indskr_date': { $lte: maxEndDateUnixTimestamp.toString() }
            }
        };

        let deletedMarketScans = [];
        try {
            const rawMarketScans = await this.diskService.find(option);

            if (rawMarketScans && Array.isArray(rawMarketScans)) {
                deletedMarketScans = rawMarketScans.filter(raw => !isEmpty(raw.indskr_date)).map(raw => {
                    // Not using constructor to avoid unnecessary overhead
                    return { _id: raw._id, _deleted: true, _rev: raw._rev };
                });
            }
        } catch (error) {
            console.error('error occurred : purge market scans: ', error);
        }
        try {
            // Bulk save/update docs to DB
            if (deletedMarketScans.length)
                await this.diskService.bulk(deletedMarketScans);
        } catch (error) {
            console.error('error occurred :saving deleted market scans: ', error);
        }
    }

    public async uploadOfflineMarketScans(isPartialUpload = false, maxRecordCountForPartialUpload = 10): Promise<any> {
        if (!isPartialUpload && this.deviceService.isOffline) return;
        let option = {
            selector: {
                '_id': {
                    $gte: DB_KEY_PREFIXES.MARKET_SCANS,
                    $lte: DB_KEY_PREFIXES.MARKET_SCANS + PREFIX_SEARCH_ENDKEY_UNICODE
                },
                'pendingPushToDynamics': { $eq: true }
            }
        };
        const marketScanSyncInfo: EntitySyncInfo = {
          entityName: EntityNames.marketScan,
          totalFailed: 0,
          totalSynced: 0,
          errors: [],
          syncStatus: true
        };
        let rawMarketScans: MarketScan[] = [];
        try {
            //Get all market scans, which has pendingPushToDynamics=true
            rawMarketScans = await this.diskService.find(option);
            if (!isEmpty(rawMarketScans)) {
                if (isPartialUpload) {
                    if (rawMarketScans.length > maxRecordCountForPartialUpload) {
                        rawMarketScans = rawMarketScans.splice(0, maxRecordCountForPartialUpload);
                    }
                }
                //While pushing to db, update Pending sync to Completed status code
                rawMarketScans.filter(marketScan =>
                    (marketScan.statuscode === MarketScanStatusCode.Active || marketScan.statuscode === MarketScanStatusCode.PendingSync)).forEach(marketScan => {
                        if (this.isExpired(marketScan.indskr_date) && marketScan.indskr_marketscanid) {
                            //Dynamics might have marked record as expired
                            marketScan.statecode = MarketScanStateCode.InActive;
                            marketScan.statuscode = MarketScanStatusCode.Expired;
                        } else {
                            marketScan.statuscode = MarketScanStatusCode.Completed
                        }
                    });
                const uploadTime = new Date().getTime().toString();
                const payload = rawMarketScans.map(item => {
                  return this._getMarketScanServicePayload(item);
                })
                await this.marketScanDataService.uploadOfflineMarketScan(payload).then((response) => {
                    if (response) {
                        for (let i = 0; i < response.length; i++) {
                            const data = response[i];
                            const indskr_externalid = data['indskr_externalid'];
                            const index = rawMarketScans.findIndex(mS => mS.indskr_externalid === indskr_externalid);
                            if (index >= 0) {
                                //errorId will bre returned if it fails to save in dynamics
                                if (data['errorId']) {
                                    marketScanSyncInfo.totalFailed++;
                                    rawMarketScans[index].statuscode = MarketScanStatusCode.PendingSync;
                                    //If invalid marketscan customer data action is DELETE
                                    if (data['errorCode'] === 'ERR_MARKET_SCAN_DELETE_01') {
                                        //Remove all deleted customer data
                                        rawMarketScans[index].marketScanCustomerDatas = rawMarketScans[index].marketScanCustomerDatas.filter(customerData => customerData.action === Action.PATCH);
                                    }
                                }
                                else if (data['indskr_marketscanid'] && !data['errorId']) {
                                    this.removeDELETEActionObjs(rawMarketScans[index]);
                                    rawMarketScans[index].indskr_marketscanid = data['indskr_marketscanid'];
                                    rawMarketScans[index].pendingPushToDynamics = false;
                                    marketScanSyncInfo.totalSynced++;
                                }
                            }
                        }
                      //If flag is set false, it is saved successfully to dynamics
                      const savedMarketScans = rawMarketScans.filter(marketScan => !marketScan.pendingPushToDynamics);
                      this.mapDeltaSyncedMarketScans(savedMarketScans, uploadTime);
                      // Track offline data count
                      const newCount = rawMarketScans.filter(marketScan => marketScan.pendingPushToDynamics);
                      this.diskService.subtractOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MARKET_SCANS, newCount ? newCount.length : 0);
                      this.deltaService.addEntitySyncInfo(marketScanSyncInfo, true);
                    }
                })
            }
        } catch (err) {
            marketScanSyncInfo.totalFailed = rawMarketScans?.length;
            console.error("Failed to upload offline marketscan: ", err);
            this.deltaService.addSyncErrorToEntitySyncInfo(marketScanSyncInfo, this.authService.userConfig.activeInstance.entryPointUrl + Endpoints.marketScan.OFFLINE_UPLOAD_MARKET_SCAN, err);
            this.deltaService.addEntitySyncInfo(marketScanSyncInfo, true);
        }
    }

    private isExpired(date: string): boolean {
        const monthEnd = this.dateTimeFormatsService.getEndOfMonth(new Date(parseInt(date)));
        const allowedUpdateDuration = this.dateTimeFormatsService.addDaysToDate(monthEnd, this.authService.user.marketScanUpdateDuration);
        return allowedUpdateDuration < new Date();
    }

    public getEndDateAsPerConf(startDate: Date): Date {
        let endDate = null;
        switch (this.authService.user.marketScanCreateFrequency) {
            case CreateFrequency.WEEKLY:
                endDate = this.dateTimeFormatsService.addDaysToDate(startDate, 6);
                break;
            case CreateFrequency.DAILY:
                endDate = startDate;
                break;
            case CreateFrequency.MONTHLY:
                endDate = this.dateTimeFormatsService.getEndOfMonth(startDate);
                break;
            default:
                endDate = this.dateTimeFormatsService.getEndOfMonth(startDate);
        }
        return new Date(endDate);
    }

    public getErrorMessageAsPerConf(startDate: Date, categoryName: string, isRecordCapturedAt? : string): string {
      let errorMessage: string = null;
      switch (this.authService.user.marketScanCreateFrequency) {
        case CreateFrequency.WEEKLY:
          errorMessage = isRecordCapturedAt ? "CUSTOMER_SCAN_FOR_WEEK_ALREADY_EXISTS_WITH_CATEGORY_WITH_ACCOUNTS" : "CUSTOMER_SCAN_FOR_WEEK_ALREADY_EXISTS_WITH_CATEGORY";
          errorMessage = this.translate.instant(errorMessage, { category: categoryName });
          break;
        case CreateFrequency.DAILY:
          errorMessage = isRecordCapturedAt ? "CUSTOMER_SCAN_FOR_DATE_ALREADY_EXISTS_WITH_CATEGORY_WITH_ACCOUNTS" : "CUSTOMER_SCAN_FOR_DATE_ALREADY_EXISTS_WITH_CATEGORY";
          errorMessage = this.translate.instant(errorMessage, { category: categoryName, date: this.getDaySuffix(this.dateTimeFormatsService.formatDate(startDate, 'D')), month: this.translate.instant(this.dateTimeFormatsService.formatDate(startDate, 'MMMM').toUpperCase()) });
          break;
        case CreateFrequency.MONTHLY:
          errorMessage = isRecordCapturedAt ? "CUSTOMER_SCAN_ALREADY_EXISTS_WITH_CATEGORY_WITH_ACCOUNTS" :  "CUSTOMER_SCAN_ALREADY_EXISTS_WITH_CATEGORY";
          errorMessage = this.translate.instant(errorMessage, { category: categoryName, month: this.translate.instant(this.dateTimeFormatsService.formatDate(startDate, 'MMMM').toUpperCase()) });
          break;
        default:
          errorMessage = isRecordCapturedAt ? "CUSTOMER_SCAN_ALREADY_EXISTS_WITH_CATEGORY_WITH_ACCOUNTS" :  "CUSTOMER_SCAN_ALREADY_EXISTS_WITH_CATEGORY";
          errorMessage = this.translate.instant("CUSTOMER_SCAN_ALREADY_EXISTS_WITH_CATEGORY", { category: categoryName });
      }
      return errorMessage;
    }

    private getDaySuffix(day: string) {
        const i = parseInt(day)
        var j = i % 10,
            k = i % 100;
        if (j == 1 && k != 11) {
            return i + "st";
        }
        if (j == 2 && k != 12) {
            return i + "nd";
        }
        if (j == 3 && k != 13) {
            return i + "rd";
        }
        return i + "th";
    }

    public showDiscardChangesPopup() {
        return this.alertService.showAlert({
            title: this.translate.instant('DISCARD_CHANGES'),
            message: this.translate.instant('CI_R_U_WANT_DISCARD_CHANGES')
        }, this.translate.instant('DISCARD')
        );
    }

    private setInactiveFlagForContacts(marketScan: MarketScan) {
      if(marketScan.contacts && marketScan.contacts.length > 0){
        marketScan.contacts.forEach((c, index) => {
          const contact = this.contactService.getContactByID(c.contactId);
          if (!contact || !contact.isActive) {
              c.isActive = false;
          }
        });
      }
    }

    private setInactiveFlagForAccounts(marketScan: MarketScan) {
      if(marketScan.accounts && marketScan.accounts.length > 0){
        marketScan.accounts.forEach(ac => {
          const account = this.accountService.getAccountById(ac.accountId);
          if (!account || (account.status === 2 || account.status == 548910003 || account.status == 548910010 || account.status == 548910011 || account.status == 548910012 || account.status == 548910013)) {
            ac.isActive = false;
          }
        });
      }
    }

    private isUnderAllowedDurationForModification(date: string) {
        if (isEmpty(date)) return true;
        const monthEnd = this.dateTimeFormatsService.getEndOfMonth(new Date(parseInt(date)));
        const allowedDuration = this.dateTimeFormatsService.addDaysToDate(monthEnd, this.authService.user.marketScanUpdateDuration);
        return allowedDuration >= new Date();
    }

    isOwner() {
        return this.getSelectedMarketScan()
            && (this.authService.user.systemUserID === this.getSelectedMarketScan().ownerId);
    }

    sortListByFielName(options, fieldName: string) {
        if (isEmpty(options)) return [];
        return options.sort((a, b) => {
            let nameA: string = a[fieldName], nameB: string = b[fieldName];
            if (!nameA || !nameB) return 1;
            nameA = nameA.toLowerCase();
            nameB = nameB.toLowerCase();
            if (nameA < nameB)
                return -1;
            if (nameA >= nameB)
                return 1;
        });
    }

    public async discardChanges() {
        if (!this.marketScanCopy) return;
        this.revertStatus();
        this.getSelectedMarketScan().isModified = false;
        this.marketScanCopy.isModified = false;
        this.upsertMarketScans(this.marketScanCopy);
        await this.updateMarketScanInPouchDB(this.marketScanCopy);
        this.setCurrentMarketScan(null); // so that revert status is not called again
    }

    private initDetailsScreenMatrix(marketScan: MarketScan, firstColumnLabel: string): MarketScanDetailsScreenMatrixModel {
        return {
            matrixId: marketScan.indskr_externalid,
            matrixColumns: [{
                columnId: marketScan.selectView,
                columnLabel: firstColumnLabel,
                columnItems: [],
            }],
        };
    }

    private buildEntityMatrixRows(
        entityRow: { collection: Array<any>; idKey: string; nameKey: string; firstColumnLabel: string },
        marketScan: MarketScan,
        metricName: string,
        metricIdProperty: string,
        metricLabelName: string,
        matrixData: MarketScanMatrixModel[],
        detailsScreenMatrixData: MarketScanDetailsScreenMatrixModel,
    ): void {
        let entityIdx = 0;
        entityRow.collection.forEach(entity => {
            if (entity.action !== Action.DELETE) {
                matrixData.push({
                    matrixId: entity[entityRow.idKey],
                    matrixName: entity[entityRow.nameKey],
                    matrixTable: [{
                        columdId: 'first_column',
                        columnItems: [{
                            columnItemId: 'first_column_item',
                            isHeader: true,
                            isTotal: false,
                            isInputField: false,
                            columnItemLabel: entityRow.firstColumnLabel,
                            columnItemSecondaryLabel: '',
                            isMoreHeight: false,
                        }]
                    }]
                });
                marketScan[metricName].forEach(item => {
                    if (item.action !== Action.DELETE) {
                        matrixData[entityIdx].matrixTable.push({
                            columdId: item[metricIdProperty],
                            columnItems: [{
                                columnItemId: 'first_column_item' + item[metricIdProperty],
                                isHeader: true,
                                isTotal: false,
                                isInputField: false,
                                columnItemLabel: item[metricLabelName],
                                columnItemSecondaryLabel: '',
                                isMoreHeight: false,
                            }]
                        });
                        if (entityIdx === 0) {
                            detailsScreenMatrixData.matrixColumns.push({
                                columnId: item[metricIdProperty],
                                columnLabel: item[metricLabelName],
                                columnItems: [],
                            });
                        }
                    }
                });
                entityIdx++;
            }
        });
    }

    private populateDetailsScreenEntityColumns(
        entityId: string,
        entityName: string,
        detailsScreenMatrixData: MarketScanDetailsScreenMatrixModel,
    ): void {
        detailsScreenMatrixData.matrixColumns.forEach((column, columnIdx) => {
            column.columnItems.push({
                columnItemId: entityId + '_' + columnIdx,
                columnItemLabel: columnIdx === 0 ? entityName : '',
                showExpandIcon: columnIdx === detailsScreenMatrixData.matrixColumns.length - 1,
                childItems: [],
            });
        });
    }

    public createUpdateMarketScanCustomerDataPayload(marketScan:MarketScan){
      let flag:boolean = this.checkIfMarketScanCustomerDataCanBePopulated(marketScan);
      if(flag){
        let matrixData:Array<MarketScanMatrixModel> = [];
        let detailsScreenMatrixData:MarketScanDetailsScreenMatrixModel;
        let msMetricName:string;
        let msMetricIdProperty:string;
        let msMetricLabelName:string;
        const isReadOnlyView = this.authService.user.systemUserID !== marketScan.ownerId || marketScan.statuscode !== MarketScanStatusCode.Active
        if(marketScan.category.indskr_capturedata == CaptureData.PRODUCT || marketScan.category.indskr_capturedata === CaptureData.PROCEDURE){
          msMetricName = 'products';
          msMetricLabelName = 'productName';
          msMetricIdProperty = 'productId';
        } else{
          return;
        }

        this._sortMarketScanMetrics(marketScan);
        if (isEmpty(marketScan.indskr_marketscanid)){
          marketScan.marketScanCustomerDatas.forEach(record => {
            record.action = Action.DELETE;
          })
        }
        if(marketScan.category.indskr_capturedatafor == CaptureDataFor.CUSTOMERS_ACCOUNTS){
          if(marketScan.selectView == 'ACCOUNT_VIEW'){
            detailsScreenMatrixData = this.initDetailsScreenMatrix(marketScan, this.translate.instant('ACCOUNTS'));
            this.buildEntityMatrixRows({ collection: marketScan.accounts, idKey: 'accountId', nameKey: 'accountName', firstColumnLabel: this.translate.instant('CUSTOMERS') }, marketScan, msMetricName, msMetricIdProperty, msMetricLabelName, matrixData, detailsScreenMatrixData);
            let msAccIdx = 0;
            marketScan.accounts.forEach((msAccount)=> {
              if(msAccount.action != Action.DELETE){
                this.populateDetailsScreenEntityColumns(msAccount.accountId, msAccount.accountName, detailsScreenMatrixData);
              }
              msAccount.affiliatedContacts.forEach((msContact,msIdx)=> {
                matrixData[msAccIdx].matrixTable[0].columnItems.push({
                  columnItemId:msContact.contactId,
                  isHeader: false,
                  isTotal: false,
                  isInputField: false,
                  columnItemLabel: msContact.contact_fullName,
                  columnItemSecondaryLabel: '',
                  isMoreHeight: false,
                  childItems:[]
                });

                detailsScreenMatrixData.matrixColumns[0].columnItems[msAccIdx].childItems.push(
                  {
                    childItemId: msContact.contactId,
                    childItemLabel: msContact.contact_fullName,
                    childItemSecondaryLabel:'',
                    isMoreHeight: true,
                    subchildItems:[]
                  }
                );
                let msProductIdx=0;
                marketScan.products.forEach((msProduct)=> {
                  if(msProduct.action != Action.DELETE){
                    matrixData[msAccIdx].matrixTable[msProductIdx + 1].columnItems.push({
                      columnItemId:msContact.contactId + msProduct.productId,
                      isHeader: false,
                      isTotal: false,
                      isInputField: false,
                      columnItemLabel:'',
                      columnItemSecondaryLabel: '',
                      isMoreHeight: false,
                      childItems:[]
                    });

                    detailsScreenMatrixData.matrixColumns[msProductIdx + 1].columnItems[msAccIdx].childItems.push(
                      {
                         childItemId: msContact.contactId + msProduct.productId,
                         childItemLabel: '',
                         childItemSecondaryLabel:'',
                         isMoreHeight: true,
                         subchildItems:[]
                      }
                    );
                    msProductIdx=msProductIdx +1;
                  }

                })
               marketScan.category.measures.forEach((msMeasure,msConIdx)=> {
                  if(msMeasure.captureFor == MeasureCaptureFor.CUSTOMERS_ACCOUNTS && msAccount.action != Action.DELETE && msMeasure.action != Action.DELETE && msContact.action != Action.DELETE){
                    matrixData[msAccIdx].matrixTable[0].columnItems[msIdx +1].childItems.push({
                      childItemID: msMeasure.measureId,
                      isHeader: false,
                      isTotal: false,
                      isInputField: false,
                      childItemLabel: msMeasure.measureName,
                      isMoreHeight: false,
                    });
                    detailsScreenMatrixData.matrixColumns[0].columnItems[msAccIdx].childItems[msIdx].subchildItems.push(
                      {
                        subchildItemID: msMeasure.measureId,
                        subchildItemLabel: msMeasure.measureName,
                        isMoreHeight: true,
                      }
                    );
                  }
                  let msProIdx = 0;
                  marketScan.products.forEach((msProduct)=> {
                    if(msMeasure.captureFor == MeasureCaptureFor.CUSTOMERS_ACCOUNTS){
                      let idx = marketScan.marketScanCustomerDatas.findIndex(c => c.indskr_assessmentcategoryid == marketScan.category.indskr_assessmentcategoryid && c.accountId == msAccount.accountId && c.contactId == msContact.contactId && c.measureId == msMeasure.measureId && c.productId == msProduct.productId);
                      let obj: MarketScanCustomerData;
                      if (idx >= 0) {
                        obj = marketScan.marketScanCustomerDatas[idx];
                        if (msMeasure.action == Action.DELETE || msAccount.action == Action.DELETE || msContact.action == Action.DELETE || msProduct.action == Action.DELETE) {
                          marketScan.marketScanCustomerDatas[idx].action = Action.DELETE;
                        } else {
                          if (isEmpty(marketScan.indskr_marketscanid)){
                            obj.action = Action.PATCH;
                          }else{
                            if(obj.action == Action.DELETE){
                              obj.action = Action.PATCH;
                            }
                          }
                        }
                      } else {
                        if (!(msMeasure.action == Action.DELETE || msAccount.action == Action.DELETE || msContact.action == Action.DELETE || msProduct.action == Action.DELETE)) {
                          obj = new MarketScanCustomerData(this.generateRandomGuid(), marketScan.category.indskr_assessmentcategoryid, msMeasure.measureId, marketScan.category.indskr_name, msMeasure.measureName, msMeasure.measureType.toString(), marketScan.category.indskr_capturedata, marketScan.category.indskr_capturedatafor, msProduct.productId, msAccount.accountId, msContact.contactId, 1, Action.PATCH, '', '', null, null);
                          marketScan.marketScanCustomerDatas.push(obj);
                        }
                      }
                      if (msAccount.action != Action.DELETE && msMeasure.action != Action.DELETE && msContact.action != Action.DELETE && msProduct.action != Action.DELETE) {
                        matrixData[msAccIdx].matrixTable[msProIdx + 1].columnItems[msIdx +1].childItems.push({
                          childItemID: obj.indskr_externalid,
                          isHeader: false,
                          isTotal: false,
                          isInputField: !isReadOnlyView,
                          childItemLabel:obj.rX ? obj.rX : (!isReadOnlyView ? null : '---'),
                          isMoreHeight: true,
                        });
                        detailsScreenMatrixData.matrixColumns[msProIdx + 1].columnItems[msAccIdx].childItems[msIdx].subchildItems.push(
                          {
                            subchildItemID: obj.indskr_externalid,
                            subchildItemLabel: obj.rX ? obj.rX : '---',
                            isMoreHeight: true,
                          }
                        );
                        msProIdx = msProIdx + 1;
                      }
                    }
                  });
                });
              });
              if(msAccount.affiliatedContacts.length > 0 && marketScan.category.measures.length > 0){
              matrixData[msAccIdx].matrixTable[0].columnItems.push({
                columnItemId:msAccount.accountId,
                isHeader: false,
                isTotal: true,
                isInputField: false,
                columnItemLabel: this.translate.instant('TOTAL'),
                columnItemSecondaryLabel: '',
                isMoreHeight: false,
                childItems:[]
              });
              let msProductIdx=0;
              marketScan.products.forEach((msProduct)=> {
                if(msProduct.action != Action.DELETE){
                  matrixData[msAccIdx].matrixTable[msProductIdx + 1].columnItems.push({
                    columnItemId: msProduct.productId,
                    isHeader: false,
                    isTotal: true,
                    isInputField: false,
                    columnItemLabel:'',
                    columnItemSecondaryLabel: '',
                    isMoreHeight: false,
                    childItems:[]
                  });
                  msProductIdx=msProductIdx +1;
                }

              })
                }
              marketScan.category.measures.forEach((msMeasure,msIdx)=> {
                if(msAccount.action != Action.DELETE&& msAccount.affiliatedContacts.length > 0  && msMeasure.action != Action.DELETE  && msMeasure.captureFor == MeasureCaptureFor.CUSTOMERS_ACCOUNTS){
                  matrixData[msAccIdx].matrixTable[0].columnItems[msAccount.affiliatedContacts.length+1].childItems.push({
                    childItemID: msMeasure.measureId,
                    isHeader: false,
                    isTotal: true,
                    isInputField: false,
                    childItemLabel: msMeasure.measureName,
                    isMoreHeight: false,
                  });

                  let msProIdx = 0;
                  marketScan.products.forEach((msProduct)=> {
                    if(msProduct.action != Action.DELETE){
                      let totalValue = '0';
                      let totalNumberValue:number = 0;
                      msAccount.affiliatedContacts.forEach(tempContact=>{
                        if(msProduct.action != Action.DELETE && tempContact.action != Action.DELETE){
                          let totalidx = marketScan.marketScanCustomerDatas.findIndex(c => c.indskr_assessmentcategoryid == marketScan.category.indskr_assessmentcategoryid && c.accountId == msAccount.accountId && c.contactId == tempContact.contactId && c.measureId == msMeasure.measureId && c.productId == msProduct.productId);
                          if(totalidx >= 0){
                            if(marketScan.marketScanCustomerDatas[totalidx].rX){
                              totalNumberValue += parseInt(marketScan.marketScanCustomerDatas[totalidx].rX);
                            }
                          }
                        }
                      })
                      matrixData[msAccIdx].matrixTable[msProIdx + 1].columnItems[msAccount.affiliatedContacts.length+1].childItems.push({
                        childItemID: 'product_item_total'+msProduct.productId,
                        isHeader: false,
                        isTotal: true,
                        isInputField: false,
                        childItemLabel:totalNumberValue == 0 ? totalValue : totalNumberValue.toString(),
                        isMoreHeight: true,
                      });
                      msProIdx = msProIdx + 1;
                    }

                  });
                }
              })

              if(msAccount.action != Action.DELETE){
                msAccIdx = msAccIdx + 1;
              }
            });
          }else if(marketScan.selectView == 'CUSTOMER_VIEW'){
            detailsScreenMatrixData = this.initDetailsScreenMatrix(marketScan, this.translate.instant('CUSTOMERS'));
            this.buildEntityMatrixRows({ collection: marketScan.contacts, idKey: 'contactId', nameKey: 'contact_fullName', firstColumnLabel: this.translate.instant('ACCOUNTS') }, marketScan, msMetricName, msMetricIdProperty, msMetricLabelName, matrixData, detailsScreenMatrixData);
            let msConIdx = 0;
            marketScan.contacts.forEach((msContact)=> {
              if(msContact.action != Action.DELETE){
                this.populateDetailsScreenEntityColumns(msContact.contactId, msContact.contact_fullName, detailsScreenMatrixData);
              }
              msContact.affiliatedAccounts.forEach((msAccount,msIdx)=> {
                  matrixData[msConIdx].matrixTable[0].columnItems.push({
                    columnItemId:msAccount.accountId,
                    isHeader: false,
                    isTotal: false,
                    isInputField: false,
                    columnItemLabel: msAccount.accountName,
                    columnItemSecondaryLabel: '',
                    isMoreHeight: false,
                    childItems:[]
                  });

                  detailsScreenMatrixData.matrixColumns[0].columnItems[msConIdx].childItems.push(
                    {
                      childItemId: msAccount.accountId,
                      childItemLabel: msAccount.accountName,
                      childItemSecondaryLabel:'',
                      isMoreHeight: true,
                      subchildItems:[]
                    }
                  );
                  let msProductIdx=0;
                  marketScan.products.forEach((msProduct)=> {
                    if(msProduct.action != Action.DELETE){
                      matrixData[msConIdx].matrixTable[msProductIdx + 1].columnItems.push({
                        columnItemId:msAccount.accountId + msProduct.productId,
                        isHeader: false,
                        isTotal: false,
                        isInputField: false,
                        columnItemLabel:'',
                        columnItemSecondaryLabel: '',
                        isMoreHeight: false,
                        childItems:[]
                      });

                      detailsScreenMatrixData.matrixColumns[msProductIdx + 1].columnItems[msConIdx].childItems.push(
                        {
                           childItemId: msAccount.accountId + msProduct.productId,
                           childItemLabel: '',
                           childItemSecondaryLabel:'',
                           isMoreHeight: true,
                           subchildItems:[]
                        }
                      );
                      msProductIdx=msProductIdx +1;
                    }

                  })
                  marketScan.category.measures.forEach((msMeasure,msAccIdx)=> {
                    if(msMeasure.captureFor == MeasureCaptureFor.CUSTOMERS_ACCOUNTS && msContact.action != Action.DELETE && msMeasure.action != Action.DELETE && msAccount.action != Action.DELETE){
                      matrixData[msConIdx].matrixTable[0].columnItems[msIdx +1].childItems.push({
                        childItemID:msMeasure.measureId,
                        isHeader: false,
                        isTotal: false,
                        isInputField: false,
                        childItemLabel: msMeasure.measureName,
                        // childItemSecondaryLabel: msMeasure.captureFor == MeasureCaptureFor.CUSTOMERS_ACCOUNTS ? msMeasure.measureName : '',
                        isMoreHeight: false,
                      });

                      detailsScreenMatrixData.matrixColumns[0].columnItems[msConIdx].childItems[msIdx].subchildItems.push(
                        {
                          subchildItemID: msMeasure.measureId,
                          subchildItemLabel:msMeasure.measureName,
                          // childItemSecondaryLabel: msMeasure.measureName,
                          isMoreHeight: true,
                        }
                      );
                    }
                    let msProIdx = 0;
                    marketScan.products.forEach((msProduct)=> {
                      if(msMeasure.captureFor == MeasureCaptureFor.CUSTOMERS_ACCOUNTS){
                        let idx = marketScan.marketScanCustomerDatas.findIndex(c => c.indskr_assessmentcategoryid == marketScan.category.indskr_assessmentcategoryid && c.accountId == msAccount.accountId && c.contactId == msContact.contactId && c.measureId == msMeasure.measureId && c.productId == msProduct.productId);
                        let obj: MarketScanCustomerData;
                        if (idx >= 0) {
                          obj = marketScan.marketScanCustomerDatas[idx];
                          if (msMeasure.action == Action.DELETE || msAccount.action == Action.DELETE || msContact.action == Action.DELETE || msProduct.action == Action.DELETE) {
                            marketScan.marketScanCustomerDatas[idx].action = Action.DELETE;
                          } else {
                            if (isEmpty(marketScan.indskr_marketscanid)){
                              obj.action = Action.PATCH;
                            }else{
                              if(obj.action == Action.DELETE){
                                obj.action = Action.PATCH;
                              }
                            }
                          }
                        } else {
                          if (!(msMeasure.action == Action.DELETE || msAccount.action == Action.DELETE || msContact.action == Action.DELETE || msProduct.action == Action.DELETE)) {
                            obj = new MarketScanCustomerData(this.generateRandomGuid(), marketScan.category.indskr_assessmentcategoryid, msMeasure.measureId, marketScan.category.indskr_name, msMeasure.measureName, msMeasure.measureType.toString(), marketScan.category.indskr_capturedata, marketScan.category.indskr_capturedatafor, msProduct.productId, msAccount.accountId, msContact.contactId, 1, Action.PATCH, '', '', null, null);
                            marketScan.marketScanCustomerDatas.push(obj);
                          }
                        }
                        if (msAccount.action != Action.DELETE && msMeasure.action != Action.DELETE && msContact.action != Action.DELETE && msProduct.action != Action.DELETE) {
                          matrixData[msConIdx].matrixTable[msProIdx + 1].columnItems[msIdx +1].childItems.push({
                            childItemID: obj.indskr_externalid,
                            isHeader: false,
                            isTotal: false,
                            isInputField: !isReadOnlyView,
                            childItemLabel: obj.rX ? obj.rX : (!isReadOnlyView ? null : '---'),
                            // columnItemSecondaryLabel: '',
                            isMoreHeight: msMeasure.captureFor == MeasureCaptureFor.CUSTOMERS_ACCOUNTS ? true : false,
                          });

                          detailsScreenMatrixData.matrixColumns[msProIdx + 1].columnItems[msConIdx].childItems[msIdx].subchildItems.push(
                            {
                              subchildItemID: obj.indskr_externalid,
                              subchildItemLabel: obj.rX ? obj.rX : '---',
                              // childItemSecondaryLabel: '',
                              isMoreHeight: true,
                            }
                          );
                          msProIdx = msProIdx + 1;
                        }
                      }

                    });
                  });
              });
              if(msContact.affiliatedAccounts.length > 0 && marketScan.category.measures.length > 0){
                matrixData[msConIdx].matrixTable[0].columnItems.push({
                  columnItemId:msContact.contactId,
                  isHeader: false,
                  isTotal: true,
                  isInputField: false,
                  columnItemLabel: this.translate.instant('TOTAL'),
                  columnItemSecondaryLabel: '',
                  isMoreHeight: false,
                  childItems:[]
                });
                let msProductIdx=0;
                marketScan.products.forEach((msProduct)=> {
                  if(msProduct.action != Action.DELETE){
                    matrixData[msConIdx].matrixTable[msProductIdx + 1].columnItems.push({
                      columnItemId: msProduct.productId,
                      isHeader: false,
                      isTotal: true,
                      isInputField: false,
                      columnItemLabel:'',
                      columnItemSecondaryLabel: '',
                      isMoreHeight: false,
                      childItems:[]
                    });
                    msProductIdx=msProductIdx +1;
                  }

                })
              }
           marketScan.category.measures.forEach((msMeasure,msAccIdx)=> {
             if(msContact.action != Action.DELETE  && msContact.affiliatedAccounts.length > 0  && msMeasure.action != Action.DELETE && msMeasure.captureFor == MeasureCaptureFor.CUSTOMERS_ACCOUNTS){
              matrixData[msConIdx].matrixTable[0].columnItems[msContact.affiliatedAccounts.length+1].childItems.push({
                childItemID: msMeasure.measureId,
                isHeader: false,
                isTotal: true,
                isInputField: false,
                childItemLabel: msMeasure.measureName,
                isMoreHeight: false,
              });

                    let msProIdx = 0;
                    marketScan.products.forEach((msProduct)=> {
                      if(msProduct.action != Action.DELETE && msMeasure.captureFor == MeasureCaptureFor.CUSTOMERS_ACCOUNTS){
                        let totalValue = '0';
                        let totalNumberValue:number = 0;
                        msContact.affiliatedAccounts.forEach(tempAccount=>{
                          if(msProduct.action != Action.DELETE && tempAccount.action != Action.DELETE){
                            let totalidx = marketScan.marketScanCustomerDatas.findIndex(c => c.indskr_assessmentcategoryid == marketScan.category.indskr_assessmentcategoryid && c.contactId == msContact.contactId && c.accountId == tempAccount.accountId && c.measureId == msMeasure.measureId && c.productId == msProduct.productId);
                            if(totalidx >= 0){
                              if(marketScan.marketScanCustomerDatas[totalidx].rX){
                                totalNumberValue += parseInt(marketScan.marketScanCustomerDatas[totalidx].rX);
                              }
                            }
                          }
                        })
                        matrixData[msConIdx].matrixTable[msProIdx + 1].columnItems[msContact.affiliatedAccounts.length+1].childItems.push({
                          childItemID: 'product_item_total'+msProduct.productId,
                          isHeader: false,
                          isTotal: true,
                          isInputField: false,
                          childItemLabel:totalNumberValue == 0 ? totalValue : totalNumberValue.toString(),
                          isMoreHeight: true,
                        });
                        msProIdx = msProIdx + 1;
                      }
                    });
                  }
              });
               marketScan.category.measures.forEach((msMeasure,msAccIdx)=> {
                 if(msMeasure.captureFor == MeasureCaptureFor.CUSTOMERS){
                  if(msContact.action != Action.DELETE && msMeasure.action != Action.DELETE){
                    matrixData[msConIdx].matrixTable[0].columnItems.push({
                      columnItemId: 'measure_item_total'+msMeasure.measureId,
                      isHeader: false,
                      isTotal: false,
                      isInputField: false,
                      columnItemLabel: msMeasure.measureName,
                      columnItemSecondaryLabel: '',
                      isMoreHeight: false,
                    });

                    detailsScreenMatrixData.matrixColumns[0].columnItems[msConIdx].childItems.push(
                      {
                        childItemId: msMeasure.measureId,
                        childItemLabel: msMeasure.measureName,
                        childItemSecondaryLabel: '',
                        isMoreHeight: false,
                      }
                    );
                  }

                  let msProIdx = 0
                  marketScan.products.forEach((msProduct)=> {
                    let idx = marketScan.marketScanCustomerDatas.findIndex(c => c.indskr_assessmentcategoryid == marketScan.category.indskr_assessmentcategoryid && !c.accountId && c.contactId == msContact.contactId && c.measureId == msMeasure.measureId && c.productId == msProduct.productId);
                    let obj: MarketScanCustomerData;
                    if (idx >= 0) {
                      obj = marketScan.marketScanCustomerDatas[idx];
                      if (msMeasure.action == Action.DELETE || msContact.action == Action.DELETE || msProduct.action == Action.DELETE) {
                        marketScan.marketScanCustomerDatas[idx].action = Action.DELETE;
                      } else {
                        if (isEmpty(marketScan.indskr_marketscanid)){
                          obj.action = Action.PATCH;
                        }else {
                          if(obj.action == Action.DELETE){
                            obj.action = Action.PATCH;
                          }
                        }
                      }
                    } else {
                      if (!(msMeasure.action == Action.DELETE || msContact.action == Action.DELETE || msProduct.action == Action.DELETE)) {
                        obj = new MarketScanCustomerData(this.generateRandomGuid(), marketScan.category.indskr_assessmentcategoryid, msMeasure.measureId, marketScan.category.indskr_name, msMeasure.measureName, msMeasure.measureType.toString(), marketScan.category.indskr_capturedata, marketScan.category.indskr_capturedatafor, msProduct.productId, '', msContact.contactId, 1, Action.PATCH, '', '', null,null);
                        marketScan.marketScanCustomerDatas.push(obj);
                      }
                    }
                    if (msMeasure.action != Action.DELETE && msContact.action != Action.DELETE && msProduct.action != Action.DELETE) {
                      matrixData[msConIdx].matrixTable[msProIdx + 1].columnItems.push({
                        columnItemId: obj.indskr_externalid,
                        isHeader: false,
                        isTotal: false,
                        isInputField: !isReadOnlyView,
                        columnItemLabel: obj.rX ? obj.rX : (!isReadOnlyView ? null : '---'),
                        columnItemSecondaryLabel: '',
                        isMoreHeight: false,
                      });
                      detailsScreenMatrixData.matrixColumns[msProIdx + 1].columnItems[msConIdx].childItems.push(
                        {
                          childItemId: obj.indskr_externalid,
                          childItemLabel: obj.rX ? obj.rX : '---',
                          childItemSecondaryLabel: '',
                          isMoreHeight: false,
                        }
                      );
                      msProIdx = msProIdx + 1;
                    }
                  });
                }
              });
              if(msContact.action != Action.DELETE){
                msConIdx = msConIdx + 1;
              }
            });
          }
        }else if(marketScan.category.indskr_capturedatafor == CaptureDataFor.ACCOUNTS){
          detailsScreenMatrixData = this.initDetailsScreenMatrix(marketScan, this.translate.instant('ACCOUNTS'));
          this.buildEntityMatrixRows({ collection: marketScan.accounts, idKey: 'accountId', nameKey: 'accountName', firstColumnLabel: this.translate.instant('MEASURES') }, marketScan, msMetricName, msMetricIdProperty, msMetricLabelName, matrixData, detailsScreenMatrixData);
          let msAccIdx = 0;
          marketScan.accounts.forEach((msAccount)=> {
            if(msAccount.action != Action.DELETE){
              this.populateDetailsScreenEntityColumns(msAccount.accountId, msAccount.accountName, detailsScreenMatrixData);
            }
            marketScan.category.measures.forEach((msMeasure,msIdx)=> {

              if(msAccount.action != Action.DELETE && msMeasure.action != Action.DELETE){
                matrixData[msAccIdx].matrixTable[0].columnItems.push({
                  columnItemId: 'first_column_item'+msMeasure.measureId,
                  isHeader: false,
                  isTotal: false,
                  isInputField: false,
                  columnItemLabel: msMeasure.measureName,
                  columnItemSecondaryLabel: '',
                  isMoreHeight: false,
                });

                detailsScreenMatrixData.matrixColumns[0].columnItems[msAccIdx].childItems.push(
                  {
                    childItemId: msMeasure.measureId,
                    childItemLabel: msMeasure.measureName,
                    childItemSecondaryLabel: '',
                    isMoreHeight: false,
                  }
                );
              }
              let msProIdx = 0;
              marketScan.products.forEach((msProduct)=> {
                //if(msMeasure.captureFor == MeasureCaptureFor.CUSTOMERS_ACCOUNTS){
                  let idx = marketScan.marketScanCustomerDatas.findIndex(c => c.indskr_assessmentcategoryid == marketScan.category.indskr_assessmentcategoryid && c.accountId == msAccount.accountId && !c.contactId && c.measureId == msMeasure.measureId && c.productId == msProduct.productId);
                  let obj: MarketScanCustomerData;
                  if (idx >= 0) {
                    obj = marketScan.marketScanCustomerDatas[idx];
                    if (msMeasure.action == Action.DELETE || msAccount.action == Action.DELETE || msProduct.action == Action.DELETE) {
                      marketScan.marketScanCustomerDatas[idx].action = Action.DELETE;
                    } else {
                      if (isEmpty(marketScan.indskr_marketscanid)){
                        obj.action = Action.PATCH;
                      }else{
                        if(obj.action == Action.DELETE){
                          obj.action = Action.PATCH
                        }
                      }
                    }
                  } else {
                    if (!(msMeasure.action == Action.DELETE || msAccount.action == Action.DELETE || msProduct.action == Action.DELETE)) {
                      obj = new MarketScanCustomerData(this.generateRandomGuid(), marketScan.category.indskr_assessmentcategoryid, msMeasure.measureId, marketScan.category.indskr_name, msMeasure.measureName, msMeasure.measureType.toString(), marketScan.category.indskr_capturedata, marketScan.category.indskr_capturedatafor, msProduct.productId, msAccount.accountId, '', 1, Action.PATCH, '', '', null,null);
                      marketScan.marketScanCustomerDatas.push(obj);
                    }
                  }
                  if (msAccount.action != Action.DELETE && msMeasure.action != Action.DELETE && msProduct.action != Action.DELETE) {
                    matrixData[msAccIdx].matrixTable[msProIdx + 1].columnItems.push({
                      columnItemId: obj.indskr_externalid,
                      isHeader: false,
                      isTotal: false,
                      isInputField: !isReadOnlyView,
                      columnItemLabel: obj && obj.rX ? obj.rX : (!isReadOnlyView ? null : '---'),
                      columnItemSecondaryLabel: '',
                      isMoreHeight: false,
                    });
                    detailsScreenMatrixData.matrixColumns[msProIdx + 1].columnItems[msAccIdx].childItems.push(
                      {
                        childItemId: obj.indskr_externalid,
                        childItemLabel: obj.rX ? obj.rX : '---',
                        childItemSecondaryLabel: '',
                        isMoreHeight: false,
                      }
                    );
                    msProIdx = msProIdx + 1;
                  }
                //}
              });
            });
            if(msAccount.action != Action.DELETE){
              msAccIdx =  msAccIdx + 1;
            }
          });
        }else if(marketScan.category.indskr_capturedatafor == CaptureDataFor.CUSTOMERS){
          detailsScreenMatrixData = this.initDetailsScreenMatrix(marketScan, this.translate.instant('CUSTOMERS'));
          this.buildEntityMatrixRows({ collection: marketScan.contacts, idKey: 'contactId', nameKey: 'contact_fullName', firstColumnLabel: this.translate.instant('MEASURES') }, marketScan, msMetricName, msMetricIdProperty, msMetricLabelName, matrixData, detailsScreenMatrixData);
          let msConIdx = 0;
          marketScan.contacts.forEach((msContact)=> {
            if(msContact.action != Action.DELETE){
              this.populateDetailsScreenEntityColumns(msContact.contactId, msContact.contact_fullName, detailsScreenMatrixData);
            }
            marketScan.category.measures.forEach((msMeasure,msIdx)=> {

              if(msContact.action != Action.DELETE && msMeasure.action != Action.DELETE){
                matrixData[msConIdx].matrixTable[0].columnItems.push({
                  columnItemId: 'first_column_item'+msMeasure.measureId,
                  isHeader: false,
                  isTotal: false,
                  isInputField: false,
                  columnItemLabel: msMeasure.measureName,
                  columnItemSecondaryLabel: '',
                  isMoreHeight: false,
                });
                detailsScreenMatrixData.matrixColumns[0].columnItems[msConIdx].childItems.push(
                  {
                    childItemId: msMeasure.measureId,
                    childItemLabel: msMeasure.measureName,
                    childItemSecondaryLabel: '',
                    isMoreHeight: false,
                  }
                );
              }
              let msProIdx = 0;
              marketScan.products.forEach((msProduct)=> {
                //if(msMeasure.captureFor == MeasureCaptureFor.CUSTOMERS_ACCOUNTS){
                  let idx = marketScan.marketScanCustomerDatas.findIndex(c => c.indskr_assessmentcategoryid == marketScan.category.indskr_assessmentcategoryid && !c.accountId && c.contactId == msContact.contactId && c.measureId == msMeasure.measureId && c.productId == msProduct.productId);
                  let obj: MarketScanCustomerData;
                  if (idx >= 0) {
                    obj = marketScan.marketScanCustomerDatas[idx];
                    if (msMeasure.action == Action.DELETE || msContact.action == Action.DELETE || msProduct.action == Action.DELETE) {
                      marketScan.marketScanCustomerDatas[idx].action = Action.DELETE;
                    } else {
                      if (isEmpty(marketScan.indskr_marketscanid)){
                        obj.action = Action.PATCH;
                      }else{
                        if(obj.action == Action.DELETE){
                          obj.action = Action.PATCH;
                        }
                      }
                    }
                  } else {
                    if (!(msMeasure.action == Action.DELETE || msContact.action == Action.DELETE || msProduct.action == Action.DELETE)) {
                      obj = new MarketScanCustomerData(this.generateRandomGuid(), marketScan.category.indskr_assessmentcategoryid, msMeasure.measureId, marketScan.category.indskr_name, msMeasure.measureName, msMeasure.measureType.toString(), marketScan.category.indskr_capturedata, marketScan.category.indskr_capturedatafor, msProduct.productId, '', msContact.contactId, 1, Action.PATCH, '', '', null,null);
                      marketScan.marketScanCustomerDatas.push(obj);
                    }
                  }
                  if (msContact.action != Action.DELETE && msMeasure.action != Action.DELETE && msProduct.action != Action.DELETE) {
                    matrixData[msConIdx].matrixTable[msProIdx + 1].columnItems.push({
                      columnItemId: obj.indskr_externalid,
                      isHeader: false,
                      isTotal: false,
                      isInputField: !isReadOnlyView,
                      columnItemLabel: obj.rX ? obj.rX : (!isReadOnlyView ? null : '---'),
                      columnItemSecondaryLabel: '',
                      isMoreHeight: false,
                    });
                    detailsScreenMatrixData.matrixColumns[msProIdx + 1].columnItems[msConIdx].childItems.push(
                      {
                        childItemId: obj.indskr_externalid,
                        childItemLabel: obj && obj.rX ? obj.rX : '---',
                        childItemSecondaryLabel: '',
                        isMoreHeight: false,
                      }
                    );
                    msProIdx = msProIdx + 1;
                  }
                //}
              });
            });
            if(msContact.action != Action.DELETE){
              msConIdx = msConIdx + 1;
            }
          });
        } else if (marketScan.category.indskr_capturedatafor == CaptureDataFor.COMPETITOR) {
          detailsScreenMatrixData = this.initDetailsScreenMatrix(marketScan, this.translate.instant('COMPETITORS'));
          this.buildEntityMatrixRows({ collection: marketScan.competitors, idKey: 'competitorId', nameKey: 'competitorName', firstColumnLabel: this.translate.instant('MEASURES') }, marketScan, msMetricName, msMetricIdProperty, msMetricLabelName, matrixData, detailsScreenMatrixData);
          let msConIdx = 0;
          marketScan.competitors.forEach((msCompetitor) => {
            if (msCompetitor.action != Action.DELETE) {
              this.populateDetailsScreenEntityColumns(msCompetitor.competitorId, msCompetitor.competitorName, detailsScreenMatrixData);
            }
            marketScan.category.measures.forEach((msMeasure, msIdx) => {
              if (msCompetitor.action != Action.DELETE && msMeasure.action != Action.DELETE) {
                matrixData[msConIdx].matrixTable[0].columnItems.push({
                  columnItemId: 'first_column_item' + msMeasure.measureId,
                  isHeader: false,
                  isTotal: false,
                  isInputField: false,
                  columnItemLabel: msMeasure.measureName,
                  columnItemSecondaryLabel: '',
                  isMoreHeight: false,
                });
                detailsScreenMatrixData.matrixColumns[0].columnItems[msConIdx].childItems.push(
                  {
                    childItemId: msMeasure.measureId,
                    childItemLabel: msMeasure.measureName,
                    childItemSecondaryLabel: '',
                    isMoreHeight: false,
                  }
                );
              }
              let msProIdx = 0;
              marketScan.products.forEach((msProduct) => {
                // ! add account id check in the below idx line from the commect c.accountID
                // let idx = marketScan.marketScanCustomerDatas.findIndex(c => c.indskr_assessmentcategoryid == marketScan.category.indskr_assessmentcategoryid && c.accountId && c.competitorId == msCompetitor.competitorId && c.measureId == msMeasure.measureId && c.productId == msProduct.productId);
                let idx = marketScan.marketScanCustomerDatas.findIndex(c => c.indskr_assessmentcategoryid == marketScan.category.indskr_assessmentcategoryid && c.competitorId == msCompetitor.competitorId && c.measureId == msMeasure.measureId && c.productId == msProduct.productId);

                let obj: MarketScanCustomerData;
                if (idx >= 0) {
                  obj = marketScan.marketScanCustomerDatas[idx];
                  if (msMeasure.action == Action.DELETE || msCompetitor.action == Action.DELETE || msProduct.action == Action.DELETE) {
                    marketScan.marketScanCustomerDatas[idx].action = Action.DELETE;
                  } else {
                    if (isEmpty(marketScan.indskr_marketscanid)) {
                      obj.action = Action.PATCH;
                    } else if(obj.accountId !== marketScan.accounts.find((account) => account.action !== Action.DELETE).accountId){
                      obj.accountId = marketScan.accounts.find((account) => account.action !== Action.DELETE).accountId;
                      obj.action = Action.PATCH;
                    }else {
                      if (obj.action == Action.DELETE) {
                        obj.action = Action.PATCH;
                      }
                    }
                  }
                } else {
                  if (!(msMeasure.action == Action.DELETE || msCompetitor.action == Action.DELETE || msProduct.action == Action.DELETE)) {
                    let accountId = marketScan.accounts.length ? marketScan.accounts.find((account) => account.action !== Action.DELETE).accountId : null
                    obj = new MarketScanCustomerData(this.generateRandomGuid(), marketScan.category.indskr_assessmentcategoryid, msMeasure.measureId, marketScan.category.indskr_name, msMeasure.measureName, msMeasure.measureType.toString(), marketScan.category.indskr_capturedata, marketScan.category.indskr_capturedatafor, msProduct.productId, accountId, '', 1, Action.PATCH, '', '', null, msCompetitor.competitorId);
                    marketScan.marketScanCustomerDatas.push(obj);
                  }
                }
                if (msCompetitor.action != Action.DELETE && msMeasure.action != Action.DELETE && msProduct.action != Action.DELETE) {
                  matrixData[msConIdx].matrixTable[msProIdx + 1].columnItems.push({
                    columnItemId: obj.indskr_externalid,
                    isHeader: false,
                    isTotal: false,
                    isInputField: !isReadOnlyView,
                    columnItemLabel: obj.rX ? obj.rX : (!isReadOnlyView ? null : '---'),
                    columnItemSecondaryLabel: '',
                    isMoreHeight: false,
                  });
                  detailsScreenMatrixData.matrixColumns[msProIdx + 1].columnItems[msConIdx].childItems.push(
                    {
                      childItemId: obj.indskr_externalid,
                      childItemLabel: obj && obj.rX ? obj.rX : '---',
                      childItemSecondaryLabel: '',
                      isMoreHeight: false,
                    }
                  );
                  msProIdx = msProIdx + 1;
                }
              });
            });
            if (msCompetitor.action != Action.DELETE) {
              msConIdx = msConIdx + 1;
            }
          });
        }
        if (isEmpty(marketScan.indskr_marketscanid)){
          marketScan.marketScanCustomerDatas = marketScan.marketScanCustomerDatas.filter(record => record.action != Action.DELETE);
        }
        this.currentMarketScanMatrixData = matrixData;
        this.currentMarketScanDetailsViewMatrix = detailsScreenMatrixData;
      }else{
        this.currentMarketScanMatrixData = [];
        this.currentMarketScanDetailsViewMatrix = null;
      }
    }

    public checkIfMarketScanCustomerDataCanBePopulated(marketScan:MarketScan):boolean {
      let flag:boolean = true;
      if(!marketScan.category){
        return false;
      }else if(marketScan.category.indskr_capturedatafor == CaptureDataFor.COMPETITOR){
        if(marketScan.competitors.filter(p => p.action != Action.DELETE).length == 0){
          return false;
        }
        if(marketScan.products.filter(p => p.action != Action.DELETE).length == 0){
          return false;
        }
        if(marketScan.accounts.filter(p => p.action != Action.DELETE).length == 0){
          return false;
        }
      }else if(marketScan.products.filter(p => p.action != Action.DELETE).length == 0){
        return false;
      }else if(marketScan.category.indskr_capturedatafor == CaptureDataFor.CUSTOMERS_ACCOUNTS){
        if(marketScan.selectView){
          if(marketScan.selectView == 'ACCOUNT_VIEW'){
            if(marketScan.accounts.filter(p => p.action != Action.DELETE).length == 0){
              return false;
            }
          }else if(marketScan.selectView == 'CUSTOMER_VIEW'){
            if(marketScan.contacts.filter(p => p.action != Action.DELETE).length == 0){
              return false;
            }
          }
        }else{
          return false;
        }
      }else if(marketScan.category.indskr_capturedatafor == CaptureDataFor.ACCOUNTS){
        if(marketScan.accounts.filter(p => p.action != Action.DELETE).length == 0){
          return false;
        }
      }else if(marketScan.category.indskr_capturedatafor == CaptureDataFor.CUSTOMERS){
        if(marketScan.contacts.filter(p => p.action != Action.DELETE).length == 0){
          return false;
        }
      }
      return flag;
    }

    private _sortMarketScanMetrics(marketScan:MarketScan){
      let compeProducts: MarketScanProduct[] = marketScan.products.filter(v => v.isCompetitorProduct);
      let otherProducts: MarketScanProduct[] = marketScan.products.filter(v => !v.isCompetitorProduct);
      otherProducts = this.sortListByFielName(otherProducts,'productName')
      otherProducts.push(...this.sortListByFielName(compeProducts,'productName'))
      marketScan.products = otherProducts;
      marketScan.accounts = this.sortListByFielName(marketScan.accounts,'accountName');
      marketScan.contacts = this.sortListByFielName(marketScan.contacts,'contact_fullName');
      if(marketScan.category && marketScan.category.indskr_capturedatafor == CaptureDataFor.CUSTOMERS_ACCOUNTS){
        if(marketScan.selectView == 'ACCOUNT_VIEW'){
          marketScan.accounts.forEach(account => {
            account.affiliatedContacts = this.sortListByFielName(account.affiliatedContacts,'contact_fullName');
          });
        }else if(marketScan.selectView == 'CUSTOMER_VIEW'){
          marketScan.contacts.forEach(contact => {
            contact.affiliatedAccounts = this.sortListByFielName(contact.affiliatedAccounts,'accountName');
          });
        }
      }
      let cusAccMeasures: Measure[] = marketScan.category.measures.filter(v => v.captureFor == MeasureCaptureFor.CUSTOMERS_ACCOUNTS);
      let cusMeasures: Measure[] = marketScan.category.measures.filter(v => v.captureFor == MeasureCaptureFor.CUSTOMERS);
      cusAccMeasures = this.sortListByFielName(cusAccMeasures,'measureName');
      cusAccMeasures.push(...this.sortListByFielName(cusMeasures,'measureName'));
      marketScan.category.measures = cusAccMeasures;
    }

    public updateSubjectBasedOnFrequency(marketScan:MarketScan,catergoryUpdated: boolean = false) {
      if (!marketScan) return '';
      if (marketScan.statuscode === MarketScanStatusCode.Active) {
        if (this.authService.hasFeatureAction(FeatureActionsMap.CUSTOMER_SCAN_AUTO_SUBJECT)) {
          if (this.authService.user.marketScanCreateFrequency === CreateFrequency.MONTHLY && marketScan.indskr_date) {
            const marketScanDateValue = new Date(parseInt(marketScan.indskr_date));
            const month = this.dateTimeFormatsService.formatDate(marketScanDateValue, 'MMMM').toUpperCase();
            const year = this.dateTimeFormatsService.formatDate(marketScanDateValue, 'YYYY');
            let categoryName:string = '';
            if (marketScan.category) {
              categoryName = marketScan.category.indskr_name.replace(" (" + this.translate.instant('INACTIVE') + ")", "");
            }
            marketScan.indskr_name = this.translate.instant("CUSTOMER_SCAN") + " - " + categoryName + " - " + this.translate.instant(month) + " " + year;
          } else {
            marketScan.indskr_name = this.translate.instant("CUSTOMER_SCAN");
          }
        } else if (catergoryUpdated) {
          marketScan.indskr_name = this.translate.instant("CUSTOMER_SCAN") + " - " + marketScan.category.indskr_name;
        }
      }
    }

    private generateRandomGuid() {
      return "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
          var r = Math.random()*16|0, v = c == 'x' ? r : (r&0x3|0x8);
          return v.toString(16);
      });
    }

    private _getMarketScanServicePayload(marketScan:MarketScan){
      if(marketScan){
        let obj = {
          indskr_externalid: marketScan.indskr_externalid,
          statecode: marketScan.statecode,
          statuscode: marketScan.statuscode,
        };
        if(marketScan.indskr_marketscanid){
          obj['indskr_marketscanid'] = marketScan.indskr_marketscanid;
        }
        if(marketScan.indskr_name){
          obj['indskr_name'] = marketScan.indskr_name;
        }
        if(marketScan.indskr_date){
          obj['indskr_date'] = marketScan.indskr_date;
        }
        if(marketScan.indskr_enddate){
          obj['indskr_enddate'] = marketScan.indskr_enddate;
        }
        if(marketScan.ownerId){
          obj['ownerId'] = marketScan.ownerId;
        }
        if(marketScan.indskr_positionid){
          obj['indskr_positionid'] = marketScan.indskr_positionid;
        }
        if(marketScan.categoryId){
          obj['categoryId'] = marketScan.categoryId;
        }
        if(marketScan.customerSegmentations && marketScan.customerSegmentations.length >= 0){
          obj['customerSegmentations'] = marketScan.customerSegmentations.filter(a=> a.action);
        }
        if(marketScan.marketScanCustomerDatas && marketScan.marketScanCustomerDatas.length >= 0){
          obj['marketScanCustomerData'] = marketScan.marketScanCustomerDatas.filter(a=> a.action).map(a=> {
            let scanObj = {
              indskr_externalid: a.indskr_externalid,
            };
            if(a.indskr_assessmentcategoryid){
              scanObj['indskr_assessmentcategoryid'] = a.indskr_assessmentcategoryid;
            }
            if(a.measureId){
              scanObj['measureId'] = a.measureId;
            }
            if(a.categoryName){
              scanObj['categoryName'] = a.categoryName;
            }
            if(a.measureName){
              scanObj['measureName'] = a.measureName;
            }
            if(a.measureType){
              scanObj['measureType'] = a.measureType;
            }
            if(a.indskr_capturedata){
              scanObj['indskr_capturedata'] = a.indskr_capturedata;
            }
            if(a.indskr_capturedatafor){
              scanObj['indskr_capturedatafor'] = a.indskr_capturedatafor;
            }
            if(a.productId){
              scanObj['productId'] = a.productId;
            }
            if(a.accountId){
              scanObj['accountId'] = a.accountId;
            }
            if(a.contactId){
              scanObj['contactId'] = a.contactId;
            }
            if(a.action){
              scanObj['action'] = a.action;
            }
            if(a.indskr_eventid){
              scanObj['indskr_eventid'] = a.indskr_eventid;
            }
            if(a.indskr_campaignid){
              scanObj['indskr_campaignid'] = a.indskr_campaignid;
            }
            if(a.rX){
              scanObj['rX'] = isInteger(a.rX)?a.rX.toString():a.rX;
            }
            if(a.competitorId){
              scanObj['competitorId'] = a.competitorId;
              scanObj['categoryName'] = a.categoryName
            }
            return scanObj;
          });
        }
        return obj;
      }
    }

  // checks if market scan already exists for the selected period and selected catergory
  checkIfMarketScanExists(selectedCategoryId: string, marketScan:MarketScan): boolean {
    const marketScanDate = marketScan.indskr_date ? new Date(parseInt(marketScan.indskr_date)).toISOString() : undefined;
    if (isEmpty(marketScanDate) || isEmpty(selectedCategoryId)) return false;
    const selectedEndDate: Date = this.getEndDateAsPerConf(new Date(marketScanDate));
    const selectedCategory = this.categories.find((category) => category.indskr_assessmentcategoryid === selectedCategoryId);
    const marketScans: MarketScan[] = this.getAllMarketScans();
    const index = marketScans.findIndex((ms) => {
      if (ms.indskr_externalid !== marketScan.indskr_externalid && ms.indskr_date) {
        //For old data, end date will be empty
        if (!ms.indskr_enddate)
          ms.indskr_enddate = this.dateTimeFormatsService.getEndOfMonth(new Date(parseInt(ms.indskr_date))).getTime().toString();

        let needToCheckAccountId = selectedCategory.indskr_recordcapturedat != null;

        if (this.authService.user.marketScanCreateFrequency === CreateFrequency.MONTHLY) {
          return ((new Date(parseInt(ms.indskr_date)).getFullYear() === new Date(marketScanDate).getFullYear()) &&
            (new Date(parseInt(ms.indskr_date)).getMonth() === new Date(marketScanDate).getMonth())) && ms.categoryId === selectedCategoryId && (!needToCheckAccountId || this.checkAccountExists(marketScan, ms));
        } else
          return this.dateRangeOverlaps(new Date(parseInt(ms.indskr_date)), new Date(parseInt(ms.indskr_enddate)), new Date(marketScanDate), selectedEndDate) && ms.categoryId === selectedCategoryId && (!needToCheckAccountId || this.checkAccountExists(marketScan, ms)) ;
      }
    });
    return index >= 0;
  }

  checkAccountExists(currentMarketScan: MarketScan, ms: MarketScan): boolean {
    const selectedMarketScanAccountIds = currentMarketScan.accounts.map((account) => account.accountId);
    const accountIds = ms.marketScanCustomerDatas.map((account) => account.accountId);
    let x = accountIds.some((accountId) => selectedMarketScanAccountIds.some(acc => acc === accountId))
    console.log('account exists',x)

    return x;
  }

  private dateRangeOverlaps(start1: Date, end1: Date, start2: Date, end2: Date) {
    //compare without time
    const a_start: number = start1.setHours(0, 0, 0, 0);
    const a_end: number = end1.setHours(0, 0, 0, 0);
    const b_start: number = start2.setHours(0, 0, 0, 0);
    const b_end: number = end2.setHours(0, 0, 0, 0);
    if (a_start <= b_start && b_start <= a_end) return true; // b starts in a
    if (a_start <= b_end && b_end <= a_end) return true; // b ends in a
    if (b_start < a_start && a_end < b_end) return true; // a in b
    return false;
  }


}
